import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import {
  BookOpen,
  Award,
  Brain,
  Users,
  Share2,
  Save,
  Code,
  Play,
  Edit3,
  Edit,
  Zap,
  Sliders,
  HelpCircle,
  Database,
  LogOut,
  User,
  BarChart2,
  Activity,
  Gift,
  Coins,
  Eye,
  Ear,
  PenTool,
  Hammer,
  AlertTriangle,
  Search,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';
import { motion, AnimatePresence } from 'framer-motion';
import './morpheus-interface.css';
import LMSDeployment from './LMSDeployment';
import NotebookPanel from './NotebookPanel';
import LMSMonitoring from './LMSMonitoring';
import TokenActivity from './TokenActivity';
import MarketplaceTutors from './MarketplaceTutors';
import TokenMarketplace from './tokens/TokenMarketplace';
import StudentRosterTable from './StudentRosterTable';
import MisconceptionAlert from './MisconceptionAlert';
import CommonQuestions from './CommonQuestions';
import DeploymentOptions from './DeploymentOptions';
import EssayUpload from './EssayUpload';
import EssayStatus from './EssayStatus';
import TappyLabsBranding from './TappyLabsBranding';
import StudentAttentionWidget from './StudentAttentionWidget';
import ToastNotification from './ToastNotification';

// Official Tappy Brand Colors - Match School/District Admin Dashboards
const TappyBrandColors = {
  primary: {
    darkTeal: '#1A3E48',
    teal: '#297A92',
    mediumTeal: '#388899',
    brandBlue: '#76909F',
    brightTeal: '#42909E',
    aqua: '#4D99A5',
    lightAqua: '#5BA3AC',
    skyBlue: '#6DB4CD',
  },
  accent: {
    coral: '#EA6D37',
    gold: '#ECC962',
    brightGold: '#F8C341',
    tan: '#C59F65',
  },
  neutral: {
    darkSlate: '#3D4954',
    deepPurple: '#391339',
    mauve: '#8D7F88',
    mintGray: '#ABCACA',
    lightGray: '#D5DEDD',
    paleGray: '#ECF3F2',
    offWhite: '#F5F9FA',
    nearWhite: '#FCFDFE',
  },
  semantic: {
    success: '#6DB4CD',
    warning: '#ECC962',
    error: '#EA6D37',
    info: '#42909E',
    primary: '#297A92',
    secondary: '#76909F',
  }
};

// Sample curriculum elements across multiple subjects
const curriculumElements = [
  { id: 1, title: 'Linear Equations', difficulty: 'Easy', topics: ['Mathematics'], bloom: 'Apply' },
  { id: 2, title: 'Essay Writing Structure', difficulty: 'Medium', topics: ['Language Arts'], bloom: 'Create' },
  { id: 3, title: 'Photosynthesis', difficulty: 'Medium', topics: ['Science'], bloom: 'Understand' },
  { id: 4, title: 'World War II Causes', difficulty: 'Hard', topics: ['History'], bloom: 'Analyze' },
  { id: 5, title: 'Shakespeare Analysis', difficulty: 'Hard', topics: ['Literature'], bloom: 'Evaluate' },
  { id: 6, title: 'Chemical Bonding', difficulty: 'Medium', topics: ['Chemistry'], bloom: 'Apply' },
  { id: 7, title: 'Poetry Interpretation', difficulty: 'Medium', topics: ['Literature'], bloom: 'Analyze' },
  { id: 8, title: 'Cell Division', difficulty: 'Hard', topics: ['Science'], bloom: 'Understand' },
];

// Learning style icons with labels
const LearningStyleIcon = ({ type, active = false, onClick }) => {
  const styles = {
    container: `flex flex-col items-center justify-center p-2 rounded-lg cursor-pointer transition-all ${
      active ? 'bg-indigo-100 text-indigo-700 ring-2 ring-indigo-500' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
    }`,
    icon: "mb-1",
    label: "text-xs font-medium"
  };

  const icons = {
    visual: <Eye size={18} className={styles.icon} />,
    auditory: <Ear size={18} className={styles.icon} />,
    reading: <PenTool size={18} className={styles.icon} />,
    kinesthetic: <Hammer size={18} className={styles.icon} />
  };

  const labels = {
    visual: "Visual",
    auditory: "Auditory",
    reading: "Reading",
    kinesthetic: "Kinesthetic"
  };

  return (
    <div className={styles.container} onClick={onClick}>
      {icons[type]}
      <span className={styles.label}>{labels[type]}</span>
    </div>
  );
};

// Enhanced Canvas Visualizer Component with KaTeX integration
const CanvasVisualizer = ({ 
  mathProblem, 
  visualizationData, 
  learningStyle,
  onBreakthrough = () => {}
}) => {
  const canvasRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [showFormula, setShowFormula] = useState(true);
  const [hasShownBreakthrough, setHasShownBreakthrough] = useState(false);
  
  // Drawing setup on canvas
  useEffect(() => {
    if (!visualizationData || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Adapt visualization based on learning style
    if (visualizationData.steps && visualizationData.steps.length > 0 && 
        currentStep < visualizationData.steps.length) {
      
      const stepData = visualizationData.steps[currentStep];
      
      // Draw step visualization
      if (stepData.drawInstructions) {
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#4F46E5';
        ctx.fillStyle = '#E0E7FF';
        
        stepData.drawInstructions.forEach(instruction => {
          if (instruction.type === 'rectangle') {
            ctx.fillRect(instruction.x, instruction.y, instruction.width, instruction.height);
            ctx.strokeRect(instruction.x, instruction.y, instruction.width, instruction.height);
          } else if (instruction.type === 'circle') {
            ctx.beginPath();
            ctx.arc(instruction.x, instruction.y, instruction.radius, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
          } else if (instruction.type === 'line') {
            ctx.beginPath();
            ctx.moveTo(instruction.x1, instruction.y1);
            ctx.lineTo(instruction.x2, instruction.y2);
            ctx.stroke();
          } else if (instruction.type === 'text') {
            ctx.font = instruction.font || '14px Arial';
            ctx.fillStyle = instruction.color || '#000000';
            ctx.fillText(instruction.text, instruction.x, instruction.y);
            ctx.fillStyle = '#E0E7FF'; // Reset fill style
          }
        });
      }
      
      // Check for breakthrough moment
      if (stepData.isBreakthrough && !hasShownBreakthrough) {
        setHasShownBreakthrough(true);
        onBreakthrough(stepData.breakthroughMessage || "You've made a breakthrough!");
      }
    }
  }, [visualizationData, currentStep, learningStyle, hasShownBreakthrough, onBreakthrough]);

  // Handle moving between steps
  const handleNextStep = () => {
    if (visualizationData?.steps && currentStep < visualizationData.steps.length - 1) {
      setCurrentStep(prevStep => prevStep + 1);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prevStep => prevStep - 1);
    }
  };

  // Format any LaTeX in the step explanation
  const formatExplanation = (text) => {
    if (!text) return "";
    
    // This is a simple implementation - in a real app, you'd use a more robust parser
    const parts = text.split(/(\$\$(.*?)\$\$|\$(.*?)\$)/g).filter(Boolean);
    
    return parts.map((part, index) => {
      if (part.startsWith('$$') && part.endsWith('$$')) {
        const formula = part.slice(2, -2);
        return <BlockMath key={index} math={formula} />;
      } else if (part.startsWith('$') && part.endsWith('$')) {
        const formula = part.slice(1, -1);
        return <InlineMath key={index} math={formula} />;
      }
      return <span key={index}>{part}</span>;
    });
  };

  if (!visualizationData || !visualizationData.steps || visualizationData.steps.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 bg-gray-50 rounded-lg p-4">
        <Brain size={32} className="text-gray-400 mb-2" />
        <p className="text-gray-500 text-center">No visualization available for this problem.</p>
      </div>
    );
  }

  const currentStepData = visualizationData.steps[currentStep];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between p-3 bg-indigo-50 border-b border-indigo-100">
        <h3 className="font-medium text-indigo-800 flex items-center">
          <Eye size={16} className="mr-2" />
          Math Visualization
        </h3>
        <div className="flex items-center space-x-2">
          <button 
            className="p-1 rounded text-xs bg-white text-indigo-600 border border-indigo-200 hover:bg-indigo-50"
            onClick={() => setShowFormula(!showFormula)}
          >
            {showFormula ? "Hide Formula" : "Show Formula"}
          </button>
        </div>
      </div>
      
      {/* Canvas visualization area */}
      <div className="p-4">
        <div className="flex justify-center mb-4">
          <canvas 
            ref={canvasRef} 
            width={400} 
            height={200} 
            className="border border-gray-200 rounded"
          ></canvas>
        </div>
        
        {/* Step controls */}
        <div className="flex items-center justify-between mb-2">
          <button 
            onClick={handlePreviousStep}
            disabled={currentStep === 0}
            className={`px-3 py-1 rounded text-sm ${
              currentStep === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
            }`}
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Step {currentStep + 1} of {visualizationData.steps.length}
          </span>
          <button 
            onClick={handleNextStep}
            disabled={currentStep === visualizationData.steps.length - 1}
            className={`px-3 py-1 rounded text-sm ${
              currentStep === visualizationData.steps.length - 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
            }`}
          >
            Next
          </button>
        </div>
        
        {/* Math formula display */}
        {showFormula && currentStepData.formula && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg overflow-x-auto">
            <BlockMath math={currentStepData.formula} />
          </div>
        )}
        
        {/* Step explanation */}
        <div className="p-3 bg-indigo-50 rounded-lg">
          <h4 className="font-medium text-sm mb-1">Step {currentStep + 1}: {currentStepData.title}</h4>
          <div className="text-sm text-gray-700">
            {formatExplanation(currentStepData.explanation)}
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced Tutor Interface Component with Learning Style Detection
const TutorInterface = () => {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    { type: 'system', content: 'Welcome to Morpheus-RL AI Tutor. How can I help you with math today?' }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [learningStyle, setLearningStyle] = useState(null);
  const [detectingStyle, setDetectingStyle] = useState(true);
  const [showBreakthrough, setShowBreakthrough] = useState(false);
  const [breakthroughMessage, setBreakthroughMessage] = useState("");
  const [currentVisualization, setCurrentVisualization] = useState(null);
  const messagesEndRef = useRef(null);

  // Mock API call for demo purposes
  const mockSendMessage = async (message) => {
    setIsLoading(true);
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Detect learning style from message
    if (detectingStyle) {
      const detectedStyle = detectLearningStyle(message);
      if (detectedStyle) {
        setLearningStyle(detectedStyle);
        setDetectingStyle(false);
      }
    }
    
    // Generate response based on message content
    let response = "I'm analyzing your question...";
    let visualization = null;
    
    // Simple keyword matching for demo
    if (message.toLowerCase().includes("quadratic")) {
      response = "Let's solve this quadratic equation. Remember that a quadratic equation has the form $ax^2 + bx + c = 0$. We can use the quadratic formula to find the solutions: $x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}$. Let's break this down step by step.";
      
      visualization = {
        steps: [
          {
            title: "Identify the coefficients",
            formula: "ax^2 + bx + c = 0",
            explanation: "First, we need to write the equation in standard form $ax^2 + bx + c = 0$ and identify the values of $a$, $b$, and $c$.",
            drawInstructions: [
              { type: 'text', text: 'Standard Form:', x: 20, y: 30, font: '16px Arial', color: '#333' },
              { type: 'text', text: 'ax² + bx + c = 0', x: 50, y: 60, font: 'bold 20px Arial', color: '#4F46E5' },
              { type: 'rectangle', x: 50, y: 80, width: 20, height: 20, color: '#4F46E5' },
              { type: 'text', text: 'a', x: 58, y: 95, font: '16px Arial', color: '#000' },
              { type: 'rectangle', x: 150, y: 80, width: 20, height: 20 },
              { type: 'text', text: 'b', x: 158, y: 95, font: '16px Arial', color: '#000' },
              { type: 'rectangle', x: 220, y: 80, width: 20, height: 20 },
              { type: 'text', text: 'c', x: 228, y: 95, font: '16px Arial', color: '#000' }
            ]
          },
          {
            title: "Apply the quadratic formula",
            formula: "x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}",
            explanation: "Now we use the quadratic formula $x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}$ to find the solutions.",
            drawInstructions: [
              { type: 'text', text: 'Quadratic Formula:', x: 20, y: 30, font: '16px Arial', color: '#333' },
              { type: 'text', text: 'x = ', x: 30, y: 80, font: 'bold 20px Arial', color: '#000' },
              { type: 'line', x1: 80, y1: 70, x2: 320, y2: 70 },
              { type: 'text', text: '-b ± √(b² - 4ac)', x: 140, y: 60, font: '16px Arial', color: '#000' },
              { type: 'text', text: '2a', x: 180, y: 90, font: '16px Arial', color: '#000' }
            ]
          },
          {
            title: "Calculate the discriminant",
            formula: "\\Delta = b^2 - 4ac",
            explanation: "The expression under the square root is called the discriminant ($\\Delta = b^2 - 4ac$). It tells us the nature of the solutions:\n- If $\\Delta > 0$, we have two real solutions.\n- If $\\Delta = 0$, we have one real solution (a repeated root).\n- If $\\Delta < 0$, we have two complex solutions.",
            drawInstructions: [
              { type: 'text', text: 'Discriminant:', x: 20, y: 30, font: '16px Arial', color: '#333' },
              { type: 'text', text: 'Δ = b² - 4ac', x: 80, y: 60, font: 'bold 20px Arial', color: '#E11D48' },
              { type: 'line', x1: 50, y1: 100, x2: 150, y2: 100 },
              { type: 'text', text: 'Δ > 0: Two real solutions', x: 50, y: 120, font: '16px Arial', color: '#059669' },
              { type: 'line', x1: 150, y1: 100, x2: 250, y2: 100 },
              { type: 'text', text: 'Δ = 0: One real solution', x: 50, y: 150, font: '16px Arial', color: '#0284C7' },
              { type: 'line', x1: 250, y1: 100, x2: 350, y2: 100 },
              { type: 'text', text: 'Δ < 0: Two complex solutions', x: 50, y: 180, font: '16px Arial', color: '#7C3AED' }
            ],
            isBreakthrough: true,
            breakthroughMessage: "Great insight! Understanding the discriminant is key to quadratic equations."
          },
          {
            title: "Find the solutions",
            formula: "x_1 = \\frac{-b + \\sqrt{\\Delta}}{2a}, \\quad x_2 = \\frac{-b - \\sqrt{\\Delta}}{2a}",
            explanation: "Finally, we calculate the two solutions using the formula. We substitute the values and compute $x_1 = \\frac{-b + \\sqrt{\\Delta}}{2a}$ and $x_2 = \\frac{-b - \\sqrt{\\Delta}}{2a}$.",
            drawInstructions: [
              { type: 'text', text: 'Solutions:', x: 20, y: 30, font: '16px Arial', color: '#333' },
              { type: 'text', text: 'x₁ = ', x: 50, y: 80, font: 'bold 18px Arial', color: '#000' },
              { type: 'line', x1: 100, y1: 70, x2: 200, y2: 70 },
              { type: 'text', text: '-b + √Δ', x: 120, y: 60, font: '16px Arial', color: '#000' },
              { type: 'text', text: '2a', x: 140, y: 90, font: '16px Arial', color: '#000' },
              { type: 'text', text: 'x₂ = ', x: 220, y: 80, font: 'bold 18px Arial', color: '#000' },
              { type: 'line', x1: 270, y1: 70, x2: 370, y2: 70 },
              { type: 'text', text: '-b - √Δ', x: 290, y: 60, font: '16px Arial', color: '#000' },
              { type: 'text', text: '2a', x: 310, y: 90, font: '16px Arial', color: '#000' }
            ]
          }
        ]
      };
    } else if (message.toLowerCase().includes("function")) {
      response = "Functions are a fundamental concept in mathematics. A function $f(x)$ maps each input $x$ to exactly one output $f(x)$. Let's explore the properties of functions, particularly focusing on their graphs and transformations.";
      
      visualization = {
        steps: [
          {
            title: "Function basics",
            formula: "f(x) = y",
            explanation: "A function is a relation that maps each input $x$ to exactly one output $y = f(x)$. We can represent functions algebraically or graphically.",
            drawInstructions: [
              { type: 'text', text: 'Function Machine:', x: 20, y: 30, font: '16px Arial', color: '#333' },
              { type: 'rectangle', x: 150, y: 40, width: 100, height: 120 },
              { type: 'text', text: 'f(x)', x: 190, y: 100, font: 'bold 18px Arial', color: '#000' },
              { type: 'line', x1: 100, y1: 100, x2: 150, y2: 100 },
              { type: 'text', text: 'x', x: 120, y: 90, font: '16px Arial', color: '#000' },
              { type: 'line', x1: 250, y1: 100, x2: 300, y2: 100 },
              { type: 'text', text: 'f(x)', x: 270, y: 90, font: '16px Arial', color: '#000' }
            ]
          },
          {
            title: "Linear functions",
            formula: "f(x) = mx + b",
            explanation: "Linear functions have the form $f(x) = mx + b$ where $m$ is the slope and $b$ is the y-intercept. The graph of a linear function is a straight line.",
            drawInstructions: [
              { type: 'text', text: 'Linear Function: f(x) = mx + b', x: 20, y: 30, font: '16px Arial', color: '#333' },
              // Draw coordinate system
              { type: 'line', x1: 50, y1: 100, x2: 350, y2: 100 }, // x-axis
              { type: 'line', x1: 200, y1: 30, x2: 200, y2: 170 }, // y-axis
              { type: 'text', text: 'x', x: 350, y: 90, font: '16px Arial', color: '#000' },
              { type: 'text', text: 'y', x: 190, y: 30, font: '16px Arial', color: '#000' },
              // Draw linear function
              { type: 'line', x1: 100, y1: 140, x2: 300, y2: 60, color: '#4F46E5' }, // line with positive slope
              { type: 'text', text: 'Slope (m)', x: 220, y: 150, font: '16px Arial', color: '#4F46E5' },
              { type: 'text', text: 'y-intercept (b)', x: 120, y: 50, font: '16px Arial', color: '#E11D48' }
            ]
          },
          {
            title: "Quadratic functions",
            formula: "f(x) = ax^2 + bx + c",
            explanation: "Quadratic functions have the form $f(x) = ax^2 + bx + c$. Their graphs are parabolas. The coefficient $a$ determines whether the parabola opens upward ($a > 0$) or downward ($a < 0$).",
            drawInstructions: [
              { type: 'text', text: 'Quadratic Function: f(x) = ax² + bx + c', x: 20, y: 30, font: '16px Arial', color: '#333' },
              // Draw coordinate system
              { type: 'line', x1: 50, y1: 100, x2: 350, y2: 100 }, // x-axis
              { type: 'line', x1: 200, y1: 30, x2: 200, y2: 170 }, // y-axis
              { type: 'text', text: 'x', x: 350, y: 90, font: '16px Arial', color: '#000' },
              { type: 'text', text: 'y', x: 190, y: 30, font: '16px Arial', color: '#000' },
              // Draw parabola (approximated with points and lines)
              { type: 'circle', x: 120, y: 150, radius: 2, color: '#4F46E5' },
              { type: 'circle', x: 140, y: 130, radius: 2, color: '#4F46E5' },
              { type: 'circle', x: 160, y: 115, radius: 2, color: '#4F46E5' },
              { type: 'circle', x: 180, y: 105, radius: 2, color: '#4F46E5' },
              { type: 'circle', x: 200, y: 100, radius: 2, color: '#4F46E5' },
              { type: 'circle', x: 220, y: 105, radius: 2, color: '#4F46E5' },
              { type: 'circle', x: 240, y: 115, radius: 2, color: '#4F46E5' },
              { type: 'circle', x: 260, y: 130, radius: 2, color: '#4F46E5' },
              { type: 'circle', x: 280, y: 150, radius: 2, color: '#4F46E5' },
              // Connect points with small lines to approximate curve
              { type: 'line', x1: 120, y1: 150, x2: 140, y2: 130, color: '#4F46E5' },
              { type: 'line', x1: 140, y1: 130, x2: 160, y2: 115, color: '#4F46E5' },
              { type: 'line', x1: 160, y1: 115, x2: 180, y2: 105, color: '#4F46E5' },
              { type: 'line', x1: 180, y1: 105, x2: 200, y2: 100, color: '#4F46E5' },
              { type: 'line', x1: 200, y1: 100, x2: 220, y2: 105, color: '#4F46E5' },
              { type: 'line', x1: 220, y1: 105, x2: 240, y2: 115, color: '#4F46E5' },
              { type: 'line', x1: 240, y1: 115, x2: 260, y2: 130, color: '#4F46E5' },
              { type: 'line', x1: 260, y1: 130, x2: 280, y2: 150, color: '#4F46E5' },
              { type: 'text', text: 'Vertex', x: 200, y: 85, font: '16px Arial', color: '#E11D48' }
            ],
            isBreakthrough: true,
            breakthroughMessage: "Excellent! Understanding how the coefficient 'a' affects the parabola is a key insight."
          },
          {
            title: "Function transformations",
            formula: "g(x) = f(x-h) + k",
            explanation: "We can transform functions in various ways:\n- Vertical shift: $g(x) = f(x) + k$ (up if $k > 0$, down if $k < 0$)\n- Horizontal shift: $g(x) = f(x-h)$ (right if $h > 0$, left if $h < 0$)\n- Reflection: $g(x) = -f(x)$ (across x-axis) or $g(x) = f(-x)$ (across y-axis)\n- Stretching/Compression: $g(x) = a \\cdot f(x)$ (stretch if $|a| > 1$, compress if $|a| < 1$)",
            drawInstructions: [
              { type: 'text', text: 'Function Transformations', x: 20, y: 30, font: '16px Arial', color: '#333' },
              // Draw coordinate system
              { type: 'line', x1: 50, y1: 100, x2: 350, y2: 100 }, // x-axis
              { type: 'line', x1: 200, y1: 30, x2: 200, y2: 170 }, // y-axis
              { type: 'text', text: 'x', x: 350, y: 90, font: '16px Arial', color: '#000' },
              { type: 'text', text: 'y', x: 190, y: 30, font: '16px Arial', color: '#000' },
              // Original function - a simple parabola
              { type: 'line', x1: 140, y1: 140, x2: 160, y2: 120, color: '#9CA3AF' },
              { type: 'line', x1: 160, y1: 120, x2: 180, y2: 108, color: '#9CA3AF' },
              { type: 'line', x1: 180, y1: 108, x2: 200, y2: 105, color: '#9CA3AF' },
              { type: 'line', x1: 200, y1: 105, x2: 220, y2: 108, color: '#9CA3AF' },
              { type: 'line', x1: 220, y1: 108, x2: 240, y2: 120, color: '#9CA3AF' },
              { type: 'line', x1: 240, y1: 120, x2: 260, y2: 140, color: '#9CA3AF' },
              { type: 'text', text: 'f(x)', x: 270, y: 140, font: '14px Arial', color: '#9CA3AF' },
              // Transformed function - shifted up
              { type: 'line', x1: 140, y1: 120, x2: 160, y2: 100, color: '#4F46E5' },
              { type: 'line', x1: 160, y1: 100, x2: 180, y2: 88, color: '#4F46E5' },
              { type: 'line', x1: 180, y1: 88, x2: 200, y2: 85, color: '#4F46E5' },
              { type: 'line', x1: 200, y1: 85, x2: 220, y2: 88, color: '#4F46E5' },
              { type: 'line', x1: 220, y1: 88, x2: 240, y2: 100, color: '#4F46E5' },
              { type: 'line', x1: 240, y1: 100, x2: 260, y2: 120, color: '#4F46E5' },
              { type: 'text', text: 'g(x) = f(x) + k', x: 270, y: 120, font: '14px Arial', color: '#4F46E5' },
              // Arrow showing the vertical shift
              { type: 'line', x1: 200, y1: 105, x2: 200, y2: 85, color: '#E11D48' },
              { type: 'text', text: 'k', x: 190, y: 95, font: '14px Arial', color: '#E11D48' }
            ]
          }
        ]
      };
    } else if (message.toLowerCase().includes("triangle")) {
      response = "Triangles are fascinating shapes with many interesting properties. Let's explore some key concepts in triangle geometry, including the Pythagorean theorem for right triangles where $a^2 + b^2 = c^2$.";
      
      visualization = {
        steps: [
          {
            title: "Triangle basics",
            formula: "\\text{Area} = \\frac{1}{2} \\times \\text{base} \\times \\text{height}",
            explanation: "A triangle is a polygon with three edges and three vertices. The sum of the interior angles is always $180°$ (or $\\pi$ radians).",
            drawInstructions: [
              { type: 'text', text: 'Triangle Properties', x: 20, y: 30, font: '16px Arial', color: '#333' },
              // Draw a triangle
              { type: 'line', x1: 100, y1: 150, x2: 300, y2: 150, color: '#4F46E5' }, // base
              { type: 'line', x1: 100, y1: 150, x2: 200, y2: 50, color: '#4F46E5' }, // left side
              { type: 'line', x1: 200, y1: 50, x2: 300, y2: 150, color: '#4F46E5' }, // right side
              // Label the angles
              { type: 'text', text: 'α', x: 110, y: 130, font: 'italic 16px Arial', color: '#E11D48' },
              { type: 'text', text: 'β', x: 200, y: 60, font: 'italic 16px Arial', color: '#E11D48' },
              { type: 'text', text: 'γ', x: 280, y: 130, font: 'italic 16px Arial', color: '#E11D48' },
              // Show angle sum
              { type: 'text', text: 'α + β + γ = 180°', x: 130, y: 180, font: '16px Arial', color: '#000' }
            ]
          },
          {
            title: "Right triangles",
            formula: "a^2 + b^2 = c^2",
            explanation: "A right triangle has one angle of $90°$ (a right angle). The Pythagorean theorem states that in a right triangle, the square of the length of the hypotenuse (the side opposite the right angle) equals the sum of squares of the other two sides.",
            drawInstructions: [
              { type: 'text', text: 'Right Triangle & Pythagorean Theorem', x: 20, y: 30, font: '16px Arial', color: '#333' },
              // Draw a right triangle
              { type: 'line', x1: 100, y1: 150, x2: 250, y2: 150, color: '#4F46E5' }, // base (a)
              { type: 'line', x1: 100, y1: 150, x2: 100, y2: 50, color: '#4F46E5' }, // height (b)
              { type: 'line', x1: 100, y1: 50, x2: 250, y2: 150, color: '#4F46E5' }, // hypotenuse (c)
              // Label the sides
              { type: 'text', text: 'a', x: 175, y: 165, font: 'italic 16px Arial', color: '#000' },
              { type: 'text', text: 'b', x: 85, y: 100, font: 'italic 16px Arial', color: '#000' },
              { type: 'text', text: 'c', x: 170, y: 100, font: 'italic 16px Arial', color: '#000' },
              // Right angle symbol
              { type: 'line', x1: 100, y1: 130, x2: 120, y2: 130, color: '#000' },
              { type: 'line', x1: 120, y1: 130, x2: 120, y2: 150, color: '#000' },
              // Pythagorean theorem
              { type: 'text', text: 'a² + b² = c²', x: 280, y: 100, font: 'bold 16px Arial', color: '#E11D48' }
            ],
            isBreakthrough: true,
            breakthroughMessage: "Great job! The Pythagorean theorem is one of the most important concepts in geometry."
          },
          {
            title: "Triangle similarity",
            formula: "\\frac{a}{a'} = \\frac{b}{b'} = \\frac{c}{c'}",
            explanation: "Two triangles are similar if their corresponding angles are equal and their corresponding sides are proportional. If triangles ABC and A'B'C' are similar, then $\\frac{AB}{A'B'} = \\frac{BC}{B'C'} = \\frac{CA}{C'A'}$.",
            drawInstructions: [
              { type: 'text', text: 'Similar Triangles', x: 20, y: 30, font: '16px Arial', color: '#333' },
              // Draw a large triangle
              { type: 'line', x1: 75, y1: 160, x2: 275, y2: 160, color: '#4F46E5' }, // base
              { type: 'line', x1: 75, y1: 160, x2: 175, y2: 60, color: '#4F46E5' }, // left side
              { type: 'line', x1: 175, y1: 60, x2: 275, y2: 160, color: '#4F46E5' }, // right side
              // Draw a smaller similar triangle
              { type: 'line', x1: 125, y1: 160, x2: 225, y2: 160, color: '#E11D48' }, // base
              { type: 'line', x1: 125, y1: 160, x2: 175, y2: 110, color: '#E11D48' }, // left side
              { type: 'line', x1: 175, y1: 110, x2: 225, y2: 160, color: '#E11D48' }, // right side
              // Label the similar triangles
              { type: 'text', text: 'A', x: 70, y: 160, font: '14px Arial', color: '#4F46E5' },
              { type: 'text', text: 'B', x: 175, y: 55, font: '14px Arial', color: '#4F46E5' },
              { type: 'text', text: 'C', x: 280, y: 160, font: '14px Arial', color: '#4F46E5' },
              { type: 'text', text: "A'", x: 120, y: 160, font: '14px Arial', color: '#E11D48' },
              { type: 'text', text: "B'", x: 175, y: 105, font: '14px Arial', color: '#E11D48' },
              { type: 'text', text: "C'", x: 230, y: 160, font: '14px Arial', color: '#E11D48' },
              // Show ratio
              { type: 'text', text: 'Corresponding sides are proportional:', x: 110, y: 190, font: '14px Arial', color: '#000' },
              { type: 'text', text: 'AB/A\'B\' = BC/B\'C\' = CA/C\'A\'', x: 110, y: 210, font: '14px Arial', color: '#000' }
            ]
          },
          {
            title: "Triangle area formulas",
            formula: "\\text{Area} = \\frac{1}{2} \\times \\text{base} \\times \\text{height}",
            explanation: "There are multiple ways to calculate the area of a triangle:\n- Half base times height: $\\text{Area} = \\frac{1}{2} \\times \\text{base} \\times \\text{height}$\n- Heron's formula: $\\text{Area} = \\sqrt{s(s-a)(s-b)(s-c)}$ where $s = \\frac{a+b+c}{2}$ (semiperimeter)\n- Using coordinates: $\\text{Area} = \\frac{1}{2} \\left| (x_1(y_2 - y_3) + x_2(y_3 - y_1) + x_3(y_1 - y_2)) \\right|$",
            drawInstructions: [
              { type: 'text', text: 'Triangle Area Calculation', x: 20, y: 30, font: '16px Arial', color: '#333' },
              // Draw a triangle
              { type: 'line', x1: 100, y1: 150, x2: 300, y2: 150, color: '#4F46E5' }, // base
              { type: 'line', x1: 100, y1: 150, x2: 200, y2: 50, color: '#4F46E5' }, // left side
              { type: 'line', x1: 200, y1: 50, x2: 300, y2: 150, color: '#4F46E5' }, // right side
              // Draw height
              { type: 'line', x1: 200, y1: 50, x2: 200, y2: 150, color: '#E11D48', dash: [5, 5] }, // height
              // Label base and height
              { type: 'text', text: 'base (b)', x: 180, y: 165, font: '14px Arial', color: '#000' },
              { type: 'text', text: 'height (h)', x: 205, y: 100, font: '14px Arial', color: '#000' },
              // Area formula
              { type: 'text', text: 'Area = ½ × b × h', x: 110, y: 190, font: 'bold 16px Arial', color: '#000' }
            ]
          }
        ]
      };
    } else if (message.toLowerCase().includes("derivative") || message.toLowerCase().includes("calculus")) {
      response = "Calculus is the mathematical study of continuous change. The derivative measures the rate at which a function is changing at a specific point. Let's explore the key concepts of derivatives and their applications.";
      
      visualization = {
        steps: [
          {
            title: "Definition of a derivative",
            formula: "f'(x) = \\lim_{h \\to 0} \\frac{f(x+h) - f(x)}{h}",
            explanation: "The derivative of a function $f(x)$ at a point $x$ is defined as the limit of the difference quotient as $h$ approaches zero: $f'(x) = \\lim_{h \\to 0} \\frac{f(x+h) - f(x)}{h}$. This represents the slope of the tangent line to the function at point $x$.",
            drawInstructions: [
              { type: 'text', text: 'Derivative as Slope of Tangent Line', x: 20, y: 30, font: '16px Arial', color: '#333' },
              // Draw coordinate system
              { type: 'line', x1: 50, y1: 100, x2: 350, y2: 100 }, // x-axis
              { type: 'line', x1: 100, y1: 30, x2: 100, y2: 170 }, // y-axis
              { type: 'text', text: 'x', x: 350, y: 90, font: '14px Arial', color: '#000' },
              { type: 'text', text: 'y', x: 90, y: 30, font: '14px Arial', color: '#000' },
              // Draw curve
              { type: 'line', x1: 100, y1: 120, x2: 120, y2: 118, color: '#4F46E5' },
              { type: 'line', x1: 120, y1: 118, x2: 140, y2: 115, color: '#4F46E5' },
              { type: 'line', x1: 140, y1: 115, x2: 160, y2: 110, color: '#4F46E5' },
              { type: 'line', x1: 160, y1: 110, x2: 180, y2: 100, color: '#4F46E5' },
              { type: 'line', x1: 180, y1: 100, x2: 200, y2: 85, color: '#4F46E5' },
              { type: 'line', x1: 200, y1: 85, x2: 220, y2: 65, color: '#4F46E5' },
              { type: 'line', x1: 220, y1: 65, x2: 240, y2: 55, color: '#4F46E5' },
              { type: 'line', x1: 240, y1: 55, x2: 260, y2: 50, color: '#4F46E5' },
              { type: 'line', x1: 260, y1: 50, x2: 280, y2: 48, color: '#4F46E5' },
              { type: 'line', x1: 280, y1: 48, x2: 300, y2: 47, color: '#4F46E5' },
              { type: 'text', text: 'f(x)', x: 310, y: 47, font: '14px Arial', color: '#4F46E5' },
              // Draw point on curve
              { type: 'circle', x: 200, y: 85, radius: 3, color: '#E11D48' },
              { type: 'text', text: 'P(x, f(x))', x: 210, y: 75, font: '14px Arial', color: '#E11D48' },
              // Draw tangent line
              { type: 'line', x1: 160, y1: 105, x2: 240, y2: 65, color: '#10B981' },
              { type: 'text', text: 'Tangent Line', x: 160, y: 60, font: '14px Arial', color: '#10B981' },
              // Draw slope explanation
              { type: 'text', text: 'Slope = f\'(x)', x: 240, y: 90, font: 'bold 14px Arial', color: '#10B981' }
            ]
          },
          {
            title: "Derivative rules",
            formula: "\\begin{align}\\frac{d}{dx}[x^n] &= nx^{n-1} \\\\ \\frac{d}{dx}[e^x] &= e^x \\\\ \\frac{d}{dx}[\\ln(x)] &= \\frac{1}{x}\\end{align}",
            explanation: "There are several key rules for finding derivatives:\n- Power rule: $\\frac{d}{dx}[x^n] = nx^{n-1}$\n- Exponential rule: $\\frac{d}{dx}[e^x] = e^x$\n- Logarithmic rule: $\\frac{d}{dx}[\\ln(x)] = \\frac{1}{x}$\n- Sum rule: $\\frac{d}{dx}[f(x) + g(x)] = f'(x) + g'(x)$\n- Product rule: $\\frac{d}{dx}[f(x)g(x)] = f'(x)g(x) + f(x)g'(x)$\n- Quotient rule: $\\frac{d}{dx}[\\frac{f(x)}{g(x)}] = \\frac{f'(x)g(x) - f(x)g'(x)}{[g(x)]^2}$\n- Chain rule: $\\frac{d}{dx}[f(g(x))] = f'(g(x))g'(x)$",
            drawInstructions: [
              { type: 'text', text: 'Common Derivative Rules', x: 20, y: 30, font: '16px Arial', color: '#333' },
              // Power rule
              { type: 'text', text: 'Power Rule:', x: 40, y: 60, font: 'bold 14px Arial', color: '#000' },
              { type: 'text', text: 'd/dx[xⁿ] = n·xⁿ⁻¹', x: 120, y: 60, font: '14px Arial', color: '#4F46E5' },
              // Exponential rule
              { type: 'text', text: 'Exponential Rule:', x: 40, y: 90, font: 'bold 14px Arial', color: '#000' },
              { type: 'text', text: 'd/dx[eˣ] = eˣ', x: 150, y: 90, font: '14px Arial', color: '#4F46E5' },
              // Logarithmic rule
              { type: 'text', text: 'Logarithmic Rule:', x: 40, y: 120, font: 'bold 14px Arial', color: '#000' },
              { type: 'text', text: 'd/dx[ln(x)] = 1/x', x: 150, y: 120, font: '14px Arial', color: '#4F46E5' },
              // Chain rule
              { type: 'text', text: 'Chain Rule:', x: 40, y: 150, font: 'bold 14px Arial', color: '#000' },
              { type: 'text', text: 'd/dx[f(g(x))] = f\'(g(x))·g\'(x)', x: 120, y: 150, font: '14px Arial', color: '#4F46E5' }
            ],
            isBreakthrough: true,
            breakthroughMessage: "Excellent! Mastering these derivative rules gives you powerful tools for calculus."
          },
          {
            title: "Finding critical points",
            formula: "f'(x) = 0 \\text{ or } f'(x) \\text{ is undefined}",
            explanation: "Critical points of a function $f(x)$ occur where the derivative $f'(x) = 0$ or where $f'(x)$ is undefined. These points are candidates for local maxima, local minima, or inflection points.",
            drawInstructions: [
              { type: 'text', text: 'Critical Points of a Function', x: 20, y: 30, font: '16px Arial', color: '#333' },
              // Draw coordinate system
              { type: 'line', x1: 50, y1: 100, x2: 350, y2: 100 }, // x-axis
              { type: 'line', x1: 100, y1: 30, x2: 100, y2: 170 }, // y-axis
              { type: 'text', text: 'x', x: 350, y: 90, font: '14px Arial', color: '#000' },
              { type: 'text', text: 'y', x: 90, y: 30, font: '14px Arial', color: '#000' },
              // Draw function with critical points
              { type: 'line', x1: 100, y1: 120, x2: 120, y2: 100, color: '#4F46E5' },
              { type: 'line', x1: 120, y1: 100, x2: 140, y2: 85, color: '#4F46E5' },
              { type: 'line', x1: 140, y1: 85, x2: 160, y2: 80, color: '#4F46E5' },
              { type: 'line', x1: 160, y1: 80, x2: 180, y2: 90, color: '#4F46E5' },
              { type: 'line', x1: 180, y1: 90, x2: 200, y2: 110, color: '#4F46E5' },
              { type: 'line', x1: 200, y1: 110, x2: 220, y2: 120, color: '#4F46E5' },
              { type: 'line', x1: 220, y1: 120, x2: 240, y2: 110, color: '#4F46E5' },
              { type: 'line', x1: 240, y1: 110, x2: 260, y2: 90, color: '#4F46E5' },
              { type: 'line', x1: 260, y1: 90, x2: 280, y2: 85, color: '#4F46E5' },
              { type: 'line', x1: 280, y1: 85, x2: 300, y2: 100, color: '#4F46E5' },
              // Mark critical points
              { type: 'circle', x: 160, y: 80, radius: 3, color: '#E11D48' }, // Local minimum
              { type: 'text', text: 'Local minimum', x: 120, y: 65, font: '12px Arial', color: '#E11D48' },
              { type: 'text', text: 'f\'(x) = 0', x: 120, y: 50, font: '12px Arial', color: '#E11D48' },
              { type: 'circle', x: 220, y: 120, radius: 3, color: '#10B981' }, // Local maximum
              { type: 'text', text: 'Local maximum', x: 220, y: 135, font: '12px Arial', color: '#10B981' },
              { type: 'text', text: 'f\'(x) = 0', x: 220, y: 150, font: '12px Arial', color: '#10B981' },
              { type: 'circle', x: 280, y: 85, radius: 3, color: '#7C3AED' }, // Inflection point
              { type: 'text', text: 'Inflection point', x: 280, y: 70, font: '12px Arial', color: '#7C3AED' },
              { type: 'text', text: 'f\'\'(x) = 0', x: 280, y: 55, font: '12px Arial', color: '#7C3AED' }
            ]
          },
          {
            title: "Applications of derivatives",
            formula: "\\text{Rate of change} = \\frac{dy}{dx}",
            explanation: "Derivatives have numerous real-world applications:\n- Rate of change: The derivative represents how quickly a quantity is changing with respect to another quantity.\n- Optimization: Finding maximum or minimum values for real-world problems.\n- Motion: Velocity is the derivative of position, and acceleration is the derivative of velocity.\n- Economics: Marginal cost, marginal revenue, and marginal profit are all applications of derivatives.",
            drawInstructions: [
              { type: 'text', text: 'Applications of Derivatives', x: 20, y: 30, font: '16px Arial', color: '#333' },
              // Motion application
              { type: 'text', text: 'Motion:', x: 30, y: 60, font: 'bold 14px Arial', color: '#000' },
              { type: 'text', text: 'Position: s(t)', x: 100, y: 60, font: '14px Arial', color: '#4F46E5' },
              { type: 'text', text: 'Velocity: v(t) = s\'(t)', x: 100, y: 80, font: '14px Arial', color: '#10B981' },
              { type: 'text', text: 'Acceleration: a(t) = v\'(t) = s\'\'(t)', x: 100, y: 100, font: '14px Arial', color: '#E11D48' },
              
              // Economics application
              { type: 'text', text: 'Economics:', x: 30, y: 130, font: 'bold 14px Arial', color: '#000' },
              { type: 'text', text: 'Cost: C(x)', x: 100, y: 130, font: '14px Arial', color: '#4F46E5' },
              { type: 'text', text: 'Marginal Cost: C\'(x)', x: 100, y: 150, font: '14px Arial', color: '#10B981' },
              { type: 'text', text: 'Measures cost of producing one more unit', x: 120, y: 170, font: '12px Arial', color: '#666' }
            ]
          }
        ]
      };
    }
    
    setMessages(prevMessages => [
      ...prevMessages,
      { type: 'user', content: message },
      { type: 'assistant', content: response, visualization: visualization }
    ]);
    
    if (visualization) {
      setCurrentVisualization(visualization);
    }
    
    setIsLoading(false);
    setInput("");
  };

  // Helper function to detect learning style from user input
  const detectLearningStyle = (message) => {
    const msg = message.toLowerCase();
    
    // Visual learners often use visual terms
    if (msg.includes("see") || msg.includes("look") || msg.includes("show") || 
        msg.includes("picture") || msg.includes("image") || msg.includes("diagram") ||
        msg.includes("graph") || msg.includes("draw") || msg.includes("visualize")) {
      return "visual";
    }
    
    // Auditory learners often prefer to hear explanations
    if (msg.includes("hear") || msg.includes("tell") || msg.includes("listen") || 
        msg.includes("sound") || msg.includes("explain") || msg.includes("say") ||
        msg.includes("audio") || msg.includes("talk")) {
      return "auditory";
    }
    
    // Reading/Writing learners prefer text-based information
    if (msg.includes("read") || msg.includes("write") || msg.includes("note") || 
        msg.includes("text") || msg.includes("list") || msg.includes("define") ||
        msg.includes("word") || msg.includes("definition")) {
      return "reading";
    }
    
    // Kinesthetic learners prefer hands-on and practical examples
    if (msg.includes("do") || msg.includes("try") || msg.includes("practice") || 
        msg.includes("example") || msg.includes("hands-on") || msg.includes("experiment") ||
        msg.includes("real") || msg.includes("application") || msg.includes("apply")) {
      return "kinesthetic";
    }
    
    // If we can't detect a clear style, return null
    return null;
  };

  // Handle sending a message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!input.trim()) return;
    
    const message = input.trim();
    await mockSendMessage(message);
  };

  // Handle breakthrough animations
  const handleBreakthrough = (message) => {
    setBreakthroughMessage(message);
    setShowBreakthrough(true);
    setTimeout(() => {
      setShowBreakthrough(false);
    }, 5000);
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden flex flex-col h-[600px]">
      <div className="bg-indigo-600 px-4 py-3 flex justify-between items-center">
        <h2 className="text-white font-semibold flex items-center">
          <Brain size={18} className="mr-2" />
          Morpheus-RL AI Tutor Platform
        </h2>
        {learningStyle && (
          <div className="flex items-center space-x-2 bg-indigo-500 px-3 py-1 rounded-full">
            <span className="text-xs text-white font-medium">Learning style:</span>
            <div className="flex space-x-1">
              <LearningStyleIcon type="visual" active={learningStyle === "visual"} />
              <LearningStyleIcon type="auditory" active={learningStyle === "auditory"} />
              <LearningStyleIcon type="reading" active={learningStyle === "reading"} />
              <LearningStyleIcon type="kinesthetic" active={learningStyle === "kinesthetic"} />
            </div>
          </div>
        )}
      </div>
      
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
        {messages.map((message, index) => (
          <div 
            key={index} 
            className={`mb-4 ${
              message.type === 'user' 
                ? 'flex justify-end' 
                : message.type === 'system' 
                  ? 'flex justify-center' 
                  : 'flex justify-start'
            }`}
          >
            {message.type === 'system' ? (
              <div className="bg-gray-200 px-4 py-2 rounded-lg text-gray-700 text-sm max-w-[80%]">
                {message.content}
              </div>
            ) : message.type === 'user' ? (
              <div className="bg-indigo-600 text-white px-4 py-2 rounded-lg max-w-[80%]">
                {message.content}
              </div>
            ) : (
              <div className="bg-white shadow-sm border border-gray-200 px-4 py-3 rounded-lg max-w-[80%]">
                {message.content}
                {message.visualization && (
                  <div className="mt-3">
                    <CanvasVisualizer
                      mathProblem={message.content}
                      visualizationData={message.visualization}
                      learningStyle={learningStyle || "visual"}
                      onBreakthrough={handleBreakthrough}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Learning style selection if not yet detected */}
      {!learningStyle && !detectingStyle && (
        <div className="p-3 bg-indigo-50 border-t border-indigo-100">
          <p className="text-sm text-gray-700 mb-2">Select your preferred learning style:</p>
          <div className="flex space-x-3">
            <LearningStyleIcon type="visual" onClick={() => setLearningStyle("visual")} />
            <LearningStyleIcon type="auditory" onClick={() => setLearningStyle("auditory")} />
            <LearningStyleIcon type="reading" onClick={() => setLearningStyle("reading")} />
            <LearningStyleIcon type="kinesthetic" onClick={() => setLearningStyle("kinesthetic")} />
          </div>
        </div>
      )}
      
      {/* Breakthrough animation */}
      <AnimatePresence>
        {showBreakthrough && (
          <motion.div 
            className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.5 }}
          >
            <div className="bg-indigo-600 text-white p-6 rounded-lg shadow-lg max-w-md mx-auto text-center">
              <div className="mb-2">
                <Zap size={48} className="mx-auto text-yellow-300" />
              </div>
              <h3 className="text-xl font-bold mb-2">Breakthrough!</h3>
              <p>{breakthroughMessage}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Input area */}
      <div className="p-3 bg-white border-t border-gray-200">
        <form onSubmit={handleSendMessage} className="flex items-center">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask a math question..."
            className="flex-1 border border-gray-300 rounded-l-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            disabled={isLoading}
          />
          <button
            type="submit"
            className={`bg-indigo-600 text-white px-4 py-2 rounded-r-lg ${
              isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-indigo-700'
            }`}
            disabled={isLoading}
          >
            {isLoading ? (
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              "Send"
            )}
          </button>
        </form>
        <div className="flex justify-between mt-2 text-xs text-gray-500">
          <span>Try asking about quadratic equations, functions, triangles, or calculus!</span>
          {learningStyle && (
            <button 
              onClick={() => {
                setLearningStyle(null);
                setDetectingStyle(false);
              }}
              className="text-indigo-600 hover:text-indigo-800"
            >
              Change learning style
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const MorpheusInterface = ({ user, onLogout, isAdmin = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const profileDropdownRef = useRef(null);

  // Get active tab from URL path
  const getActiveTabFromPath = () => {
    const path = location.pathname;
    if (path.includes('/class-overview')) return 'analytics';
    if (path.includes('/dashboard')) return 'dashboard';
    if (path.includes('/designer')) return 'designer';
    if (path.includes('/trainer')) return 'trainer';
    if (path.includes('/marketplace')) return 'marketplace';
    if (path.includes('/tokens')) return 'tokens';
    if (path.includes('/deployment')) return 'deployment';
    return 'dashboard'; // default
  };

  const [activeTab, setActiveTab] = useState(getActiveTabFromPath());

  // Update activeTab when URL changes
  useEffect(() => {
    setActiveTab(getActiveTabFromPath());
  }, [location.pathname]);

  // Helper to navigate to a tab
  const navigateToTab = (tab) => {
    const routes = {
      dashboard: '/teacher/dashboard',
      analytics: '/teacher/class-overview',
      designer: '/teacher/designer',
      trainer: '/teacher/trainer',
      marketplace: '/teacher/marketplace',
      tokens: '/teacher/tokens',
      deployment: '/teacher/deployment'
    };
    navigate(routes[tab] || routes.dashboard);
  };
  const [deploymentTab, setDeploymentTab] = useState('integration');
  const [selectedSubject, setSelectedSubject] = useState('math');
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showEssayUpload, setShowEssayUpload] = useState(false);
  const [showEssayStatus, setShowEssayStatus] = useState(false);
  const [selectedSpecialization, setSelectedSpecialization] = useState('algebra');
  const [trainingProgress, setTrainingProgress] = useState(0);
  const [currentCurriculum, setCurrentCurriculum] = useState(curriculumElements);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isTraining, setIsTraining] = useState(false);
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [deploymentType, setDeploymentType] = useState(null);
  const [showDeploymentModal, setShowDeploymentModal] = useState(false);

  // Alert notification state
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('info');
  const [lastCheckedAlertTime, setLastCheckedAlertTime] = useState(Date.now());

  // Dashboard data state
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState(() => {
    // Load from localStorage or default to 'week'
    return localStorage.getItem('teacherAnalyticsPeriod') || 'week';
  });

  // Simulate training progress
  useEffect(() => {
    if (isTraining && trainingProgress < 100) {
      const timer = setTimeout(() => {
        setTrainingProgress(prev => Math.min(prev + 5, 100));
      }, 1000);
      return () => clearTimeout(timer);
    } else if (trainingProgress >= 100) {
      setIsTraining(false);
    }
  }, [isTraining, trainingProgress]);

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
    };

    if (showProfileDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileDropdown]);

  // Poll for new alerts (content moderation + learning analytics)
  useEffect(() => {
    if (!user || !user.id) return;

    const checkForAlerts = async () => {
      try {
        // Check for new misconceptions (high priority)
        const response = await fetch('/api/moderation/teacher/misconceptions', {
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();

          // Check if there are new high-priority misconceptions
          if (data.misconceptions && data.misconceptions.length > 0) {
            const highPriority = data.misconceptions.filter(m =>
              m.priority === 'high' || m.priority === 'critical'
            );

            if (highPriority.length > 0) {
              const alert = highPriority[0];
              setAlertMessage(
                `${alert.students_affected} students struggling with ${alert.topic}. ROI: ${alert.recommendation?.roi || 'N/A'}x`
              );
              setAlertType('warning');
              setShowAlert(true);
            }
          }
        }
      } catch (error) {
        console.error('Error checking for alerts:', error);
      }
    };

    // Check immediately on mount
    checkForAlerts();

    // Then poll every 15 seconds
    const interval = setInterval(checkForAlerts, 15000);

    return () => clearInterval(interval);
  }, [user]);

  // Fetch dashboard data on mount and when activeTab changes to dashboard
  useEffect(() => {
    if (activeTab === 'dashboard' && user && user.id) {
      fetchDashboardData();
    }
  }, [activeTab, user, analyticsPeriod]);

  const fetchDashboardData = async () => {
    try {
      setDashboardLoading(true);
      setDashboardError(null);

      const response = await fetch(`/api/moderation/teacher/class-overview?period=${analyticsPeriod}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const data = await response.json();
      setDashboardData(data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setDashboardError(error.message);
    } finally {
      setDashboardLoading(false);
    }
  };

  const handlePeriodChange = (newPeriod) => {
    setAnalyticsPeriod(newPeriod);
    localStorage.setItem('teacherAnalyticsPeriod', newPeriod);
  };

  const startTraining = () => {
    setTrainingProgress(0);
    setIsTraining(true);
  };

  const handleDropdownAction = (action) => {
    setShowProfileDropdown(false);
    
    switch(action) {
      case 'upload-essay':
        setShowEssayUpload(true);
        break;
      case 'my-essays':
        setShowEssayStatus(true);
        break;
      case 'account-settings':
        window.location.href = '/subscription';
        break;
      case 'logout':
        onLogout();
        break;
      default:
        console.warn('Unknown dropdown action:', action);
        break;
    }
  };
  
  const handleSelectTutor = (tutor) => {
    setSelectedTutor(tutor);
  };
  
  const handleDeployTutor = (type, tutor) => {
    setSelectedTutor(tutor);
    setDeploymentType(type);
    
    if (type === 'lms') {
      // For LMS deployment, switch to the LMS deployment tab
      setActiveTab('deployment');
      setDeploymentTab('integration');
    } else {
      // For AWS or local deployment, show the deployment modal
      setShowDeploymentModal(true);
    }
  };
  
  const handleDeploymentSuccess = (deploymentData) => {
    // In a real implementation, this would update the UI with the new deployment
    console.log('Deployment successful:', deploymentData);
    
    // Close the modal after a delay
    setTimeout(() => {
      setShowDeploymentModal(false);
    }, 5000);
  };

  const renderDashboard = () => {
    // Loading state
    if (dashboardLoading) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          gap: '1rem'
        }}>
          <Activity size={48} style={{ color: TappyLabsBranding.colors.primary.learningAquariumBlue, animation: 'pulse 2s infinite' }} />
          <p style={{
            fontFamily: TappyLabsBranding.typography.fonts.body,
            fontSize: TappyLabsBranding.typography.scale.lg,
            color: TappyLabsBranding.colors.semantic.neutral
          }}>Loading your impact data...</p>
        </div>
      );
    }

    // Error state
    if (dashboardError) {
      return (
        <div style={{
          ...TappyLabsBranding.components.glassCard,
          padding: '2rem',
          textAlign: 'center'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            margin: '0 auto 1rem',
            borderRadius: '50%',
            backgroundColor: `${TappyLabsBranding.colors.secondary.gentleCoral}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <HelpCircle size={32} style={{ color: TappyLabsBranding.colors.secondary.gentleCoral }} />
          </div>
          <h3 style={{
            fontFamily: TappyLabsBranding.typography.fonts.headers,
            fontSize: TappyLabsBranding.typography.scale.xl,
            fontWeight: TappyLabsBranding.typography.weights.bold,
            color: TappyLabsBranding.colors.primary.deepOcean,
            marginBottom: '0.5rem'
          }}>Unable to Load Dashboard</h3>
          <p style={{
            fontFamily: TappyLabsBranding.typography.fonts.body,
            fontSize: TappyLabsBranding.typography.scale.base,
            color: TappyLabsBranding.colors.semantic.neutral,
            marginBottom: '1.5rem'
          }}>{dashboardError}</p>
          <button
            onClick={fetchDashboardData}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: TappyLabsBranding.colors.primary.learningAquariumBlue,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: TappyLabsBranding.typography.scale.base,
              fontWeight: TappyLabsBranding.typography.weights.medium,
              cursor: 'pointer',
              fontFamily: TappyLabsBranding.typography.fonts.body
            }}
          >
            Try Again
          </button>
        </div>
      );
    }

    // Calculate metrics from dashboard data
    const engagement = dashboardData?.engagement || {};
    const misconceptions = dashboardData?.misconceptions || [];
    const totalHours = (engagement.total_minutes || 0) / 60;
    const studentsActive = engagement.students_active || 0;
    const studentsEnrolled = engagement.students_enrolled || 0;
    const adoptionRate = studentsEnrolled > 0 ? (studentsActive / studentsEnrolled * 100) : 0;

    // ROI calculations (traditional tutoring at $50/hour)
    const tutoringSavings = totalHours * 50;
    const roiMultiple = tutoringSavings > 0 ? tutoringSavings / 100 : 0; // Assuming $100 base cost

    // Misconception metrics
    const totalMisconceptions = misconceptions.length;
    const highPriorityMisconceptions = misconceptions.filter(m => m.priority === 'high' || m.priority === 'critical').length;
    const totalStudentsImpacted = misconceptions.reduce((sum, m) => sum + (m.students_affected || 0), 0);
    const totalTimeSaveable = misconceptions.reduce((sum, m) => sum + (m.recommendation?.time_saved_minutes || 0), 0);

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '2rem' }}>
        {/* Impact Metrics Card */}
        <div style={{
          ...TappyLabsBranding.components.glassCard,
          padding: '2rem'
        }}>
          <h2 style={{
            fontSize: TappyLabsBranding.typography.scale.xl,
            fontWeight: TappyLabsBranding.typography.weights.bold,
            fontFamily: TappyLabsBranding.typography.fonts.headers,
            color: TappyBrandColors.neutral.darkSlate,
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Award style={{ marginRight: '0.5rem', color: TappyBrandColors.accent.gold }} size={20} />
            Tappy Usage This Week
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div>
              <div style={{
                fontSize: TappyLabsBranding.typography.scale['3xl'],
                fontWeight: TappyLabsBranding.typography.weights.bold,
                color: TappyBrandColors.primary.teal,
                fontFamily: TappyLabsBranding.typography.fonts.headers
              }}>
                {totalHours.toFixed(1)}
              </div>
              <div style={{
                fontSize: TappyLabsBranding.typography.scale.sm,
                color: TappyBrandColors.primary.brandBlue,
                fontFamily: TappyLabsBranding.typography.fonts.body
              }}>
                Learning Hours
              </div>
            </div>

            <div style={{
              height: '1px',
              backgroundColor: TappyBrandColors.neutral.lightGray
            }} />

            <div>
              <div style={{
                fontSize: TappyLabsBranding.typography.scale['2xl'],
                fontWeight: TappyLabsBranding.typography.weights.bold,
                color: TappyBrandColors.semantic.success,
                fontFamily: TappyLabsBranding.typography.fonts.headers
              }}>
                {studentsActive}
              </div>
              <div style={{
                fontSize: TappyLabsBranding.typography.scale.sm,
                color: TappyBrandColors.primary.brandBlue,
                fontFamily: TappyLabsBranding.typography.fonts.body
              }}>
                Active Students
              </div>
            </div>

            <div style={{
              padding: '1rem',
              backgroundColor: `${TappyBrandColors.primary.teal}15`,
              borderRadius: '8px',
              border: `1px solid ${TappyBrandColors.primary.teal}40`
            }}>
              <div style={{
                fontSize: TappyLabsBranding.typography.scale.xl,
                fontWeight: TappyLabsBranding.typography.weights.bold,
                color: TappyBrandColors.primary.teal,
                fontFamily: TappyLabsBranding.typography.fonts.headers
              }}>
                {Math.round((totalHours / Math.max(studentsActive, 1)) * 60)} min
              </div>
              <div style={{
                fontSize: TappyLabsBranding.typography.scale.sm,
                color: TappyBrandColors.neutral.darkSlate,
                fontFamily: TappyLabsBranding.typography.fonts.body
              }}>
                Avg. Independent Learning per Student
              </div>
            </div>
          </div>
        </div>

        {/* Teaching Wins Card */}
        <div style={{
          ...TappyLabsBranding.components.glassCard,
          padding: '2rem'
        }}>
          <h2 style={{
            fontSize: TappyLabsBranding.typography.scale.xl,
            fontWeight: TappyLabsBranding.typography.weights.bold,
            fontFamily: TappyLabsBranding.typography.fonts.headers,
            color: TappyBrandColors.neutral.darkSlate,
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Zap style={{ marginRight: '0.5rem', color: TappyBrandColors.primary.darkTeal }} size={20} />
            Teaching Wins
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{
              padding: '1rem',
              backgroundColor: TappyBrandColors.neutral.offWhite,
              borderRadius: '12px',
              border: `1px solid ${TappyBrandColors.neutral.lightGray}`
            }}>
              <div style={{
                fontSize: TappyLabsBranding.typography.scale['2xl'],
                fontWeight: TappyLabsBranding.typography.weights.bold,
                color: TappyBrandColors.primary.darkTeal,
                fontFamily: TappyLabsBranding.typography.fonts.headers,
                marginBottom: '0.25rem'
              }}>
                {totalMisconceptions}
              </div>
              <div style={{
                fontSize: TappyLabsBranding.typography.scale.sm,
                color: TappyBrandColors.neutral.darkSlate,
                fontFamily: TappyLabsBranding.typography.fonts.body,
                marginBottom: '0.5rem'
              }}>
                Misconceptions Caught Early
              </div>
              {highPriorityMisconceptions > 0 && (
                <div style={{
                  fontSize: TappyLabsBranding.typography.scale.xs,
                  color: TappyBrandColors.accent.coral,
                  fontFamily: TappyLabsBranding.typography.fonts.body,
                  fontWeight: TappyLabsBranding.typography.weights.medium
                }}>
                  {highPriorityMisconceptions} high priority
                </div>
              )}
            </div>

            <div style={{
              padding: '1rem',
              backgroundColor: TappyBrandColors.neutral.offWhite,
              borderRadius: '12px',
              border: `1px solid ${TappyBrandColors.neutral.lightGray}`
            }}>
              <div style={{
                fontSize: TappyLabsBranding.typography.scale['2xl'],
                fontWeight: TappyLabsBranding.typography.weights.bold,
                color: TappyBrandColors.primary.teal,
                fontFamily: TappyLabsBranding.typography.fonts.headers,
                marginBottom: '0.25rem'
              }}>
                {Math.round(totalTimeSaveable)}
              </div>
              <div style={{
                fontSize: TappyLabsBranding.typography.scale.sm,
                color: TappyBrandColors.neutral.darkSlate,
                fontFamily: TappyLabsBranding.typography.fonts.body
              }}>
                Minutes of Re-teaching Time Saved
              </div>
            </div>

            <div style={{
              padding: '1rem',
              backgroundColor: TappyBrandColors.neutral.offWhite,
              borderRadius: '12px',
              border: `1px solid ${TappyBrandColors.neutral.lightGray}`
            }}>
              <div style={{
                fontSize: TappyLabsBranding.typography.scale['2xl'],
                fontWeight: TappyLabsBranding.typography.weights.bold,
                color: TappyBrandColors.semantic.success,
                fontFamily: TappyLabsBranding.typography.fonts.headers,
                marginBottom: '0.25rem'
              }}>
                {totalStudentsImpacted}
              </div>
              <div style={{
                fontSize: TappyLabsBranding.typography.scale.sm,
                color: TappyBrandColors.neutral.darkSlate,
                fontFamily: TappyLabsBranding.typography.fonts.body
              }}>
                Students Protected from Confusion
              </div>
            </div>
          </div>
        </div>

        {/* Student Success Card */}
        <div style={{
          ...TappyLabsBranding.components.glassCard,
          padding: '2rem'
        }}>
          <h2 style={{
            fontSize: TappyLabsBranding.typography.scale.xl,
            fontWeight: TappyLabsBranding.typography.weights.bold,
            fontFamily: TappyLabsBranding.typography.fonts.headers,
            color: TappyBrandColors.neutral.darkSlate,
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Users style={{ marginRight: '0.5rem', color: TappyBrandColors.semantic.success }} size={20} />
            Student Engagement
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem',
              backgroundColor: TappyBrandColors.neutral.offWhite,
              borderRadius: '12px',
              border: `1px solid ${TappyBrandColors.neutral.lightGray}`
            }}>
              <div>
                <div style={{
                  fontSize: TappyLabsBranding.typography.scale.base,
                  color: TappyBrandColors.primary.brandBlue,
                  fontFamily: TappyLabsBranding.typography.fonts.body,
                  marginBottom: '0.25rem'
                }}>
                  Adoption Rate
                </div>
                <div style={{
                  fontSize: TappyLabsBranding.typography.scale['2xl'],
                  fontWeight: TappyLabsBranding.typography.weights.bold,
                  color: TappyBrandColors.primary.teal,
                  fontFamily: TappyLabsBranding.typography.fonts.headers
                }}>
                  {adoptionRate.toFixed(0)}%
                </div>
              </div>
              <div style={{
                fontSize: TappyLabsBranding.typography.scale.sm,
                color: TappyBrandColors.primary.brandBlue,
                fontFamily: TappyLabsBranding.typography.fonts.body,
                textAlign: 'right'
              }}>
                <div>{studentsActive} of {studentsEnrolled}</div>
                <div>students active</div>
              </div>
            </div>

            <div style={{
              padding: '1rem',
              backgroundColor: TappyBrandColors.neutral.offWhite,
              borderRadius: '12px',
              border: `1px solid ${TappyBrandColors.neutral.lightGray}`
            }}>
              <div style={{
                fontSize: TappyLabsBranding.typography.scale.base,
                color: TappyBrandColors.primary.brandBlue,
                fontFamily: TappyLabsBranding.typography.fonts.body,
                marginBottom: '0.25rem'
              }}>
                Questions Asked
              </div>
              <div style={{
                fontSize: TappyLabsBranding.typography.scale['2xl'],
                fontWeight: TappyLabsBranding.typography.weights.bold,
                color: TappyBrandColors.primary.darkTeal,
                fontFamily: TappyLabsBranding.typography.fonts.headers
              }}>
                {engagement.total_questions || 0}
              </div>
            </div>

            <div style={{
              padding: '1rem',
              backgroundColor: `${TappyBrandColors.semantic.success}15`,
              borderRadius: '12px',
              border: `1px solid ${TappyBrandColors.semantic.success}40`
            }}>
              <div style={{
                fontSize: TappyLabsBranding.typography.scale.sm,
                color: TappyBrandColors.neutral.darkSlate,
                fontFamily: TappyLabsBranding.typography.fonts.body,
                fontWeight: TappyLabsBranding.typography.weights.medium
              }}>
                Students are actively engaging with Teach Tappy this week
              </div>
            </div>
          </div>
        </div>

        {/* Platform Value Card */}
        <div style={{
          ...TappyLabsBranding.components.glassCard,
          padding: '2rem'
        }}>
          <h2 style={{
            fontSize: TappyLabsBranding.typography.scale.xl,
            fontWeight: TappyLabsBranding.typography.weights.bold,
            fontFamily: TappyLabsBranding.typography.fonts.headers,
            color: TappyBrandColors.neutral.darkSlate,
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Share2 style={{ marginRight: '0.5rem', color: TappyBrandColors.accent.gold }} size={20} />
            Shareable Stats
          </h2>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            <div style={{
              padding: '1.25rem',
              backgroundColor: `${TappyBrandColors.primary.teal}10`,
              borderRadius: '12px',
              border: `2px solid ${TappyBrandColors.primary.teal}30`
            }}>
              <div style={{
                fontSize: TappyLabsBranding.typography.scale.base,
                color: TappyBrandColors.neutral.darkSlate,
                fontFamily: TappyLabsBranding.typography.fonts.body,
                lineHeight: 1.6
              }}>
                "My students used Teach Tappy for <strong>{totalHours.toFixed(1)} hours</strong> of AI-powered learning this week"
              </div>
            </div>

            <div style={{
              padding: '1.25rem',
              backgroundColor: `${TappyBrandColors.primary.darkTeal}10`,
              borderRadius: '12px',
              border: `2px solid ${TappyBrandColors.primary.darkTeal}30`
            }}>
              <div style={{
                fontSize: TappyLabsBranding.typography.scale.base,
                color: TappyBrandColors.neutral.darkSlate,
                fontFamily: TappyLabsBranding.typography.fonts.body,
                lineHeight: 1.6
              }}>
                "Tappy caught <strong>{totalMisconceptions} misconceptions</strong> early, saving <strong>{Math.round(totalTimeSaveable)} minutes</strong> of re-teaching time"
              </div>
            </div>

            <div style={{
              padding: '1.25rem',
              backgroundColor: `${TappyBrandColors.semantic.success}10`,
              borderRadius: '12px',
              border: `2px solid ${TappyBrandColors.semantic.success}30`
            }}>
              <div style={{
                fontSize: TappyLabsBranding.typography.scale.base,
                color: TappyBrandColors.neutral.darkSlate,
                fontFamily: TappyLabsBranding.typography.fonts.body,
                lineHeight: 1.6
              }}>
                "<strong>{dashboardData?.engagement?.students_active || studentsActive} of {dashboardData?.engagement?.students_enrolled || studentsActive}</strong> students are actively engaged - that's <strong>{Math.round(((dashboardData?.engagement?.students_active || studentsActive) / Math.max(dashboardData?.engagement?.students_enrolled || studentsActive, 1)) * 100)}%</strong> adoption"
              </div>
            </div>

            <div style={{
              marginTop: '0.5rem',
              padding: '0.75rem',
              textAlign: 'center',
              fontSize: TappyLabsBranding.typography.scale.sm,
              color: TappyBrandColors.primary.brandBlue,
              fontFamily: TappyLabsBranding.typography.fonts.body,
              fontStyle: 'italic'
            }}>
              Perfect for sharing with colleagues and celebrating wins
            </div>
          </div>
        </div>

        {/* Priority Students Banner */}
        {dashboardData?.priority_students && dashboardData.priority_students.length > 0 && (
          <div style={{
            ...TappyLabsBranding.components.glassCard,
            padding: '1.5rem',
            marginTop: '2rem',
            border: `2px solid ${TappyBrandColors.accent.coral}40`,
            backgroundColor: `${TappyBrandColors.accent.coral}05`
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '1rem'
            }}>
              <AlertTriangle style={{ marginRight: '0.75rem', color: TappyBrandColors.accent.coral }} size={24} />
              <h3 style={{
                fontSize: TappyLabsBranding.typography.scale.lg,
                fontWeight: TappyLabsBranding.typography.weights.bold,
                fontFamily: TappyLabsBranding.typography.fonts.headers,
                color: TappyBrandColors.neutral.darkSlate,
                margin: 0
              }}>
                {dashboardData.priority_students.length} {dashboardData.priority_students.length === 1 ? 'Student Needs' : 'Students Need'} Attention This Week
              </h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {dashboardData.priority_students.slice(0, 3).map((student, idx) => (
                <div key={student.student_id} style={{
                  padding: '1rem',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: `1px solid ${TappyBrandColors.neutral.lightGray}`
                }}>
                  <div style={{
                    fontFamily: TappyLabsBranding.typography.fonts.body,
                    fontSize: TappyLabsBranding.typography.scale.sm
                  }}>
                    <span style={{
                      fontWeight: TappyLabsBranding.typography.weights.semibold,
                      color: TappyBrandColors.neutral.darkSlate
                    }}>{student.name}</span>
                    <span style={{ color: TappyBrandColors.primary.brandBlue }}> - </span>
                    <span style={{ color: TappyBrandColors.neutral.darkSlate }}>
                      {student.reasons[0]}
                    </span>
                  </div>
                </div>
              ))}
              {dashboardData.priority_students.length > 3 && (
                <div style={{
                  textAlign: 'center',
                  padding: '0.5rem',
                  fontSize: TappyLabsBranding.typography.scale.sm,
                  color: TappyBrandColors.primary.brandBlue,
                  fontFamily: TappyLabsBranding.typography.fonts.body
                }}>
                  +{dashboardData.priority_students.length - 3} more students • View full list in Class Overview →
                </div>
              )}
            </div>
          </div>
        )}

        {/* New Section: Independence & Question Quality */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '2rem', marginTop: '2rem' }}>
          {/* Class Independence Distribution */}
          <div style={{
            ...TappyLabsBranding.components.glassCard,
            padding: '2rem'
          }}>
            <h2 style={{
              fontSize: TappyLabsBranding.typography.scale.xl,
              fontWeight: TappyLabsBranding.typography.weights.bold,
              fontFamily: TappyLabsBranding.typography.fonts.headers,
              color: TappyBrandColors.neutral.darkSlate,
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Brain style={{ marginRight: '0.5rem', color: TappyBrandColors.primary.teal }} size={20} />
              Learning Independence
            </h2>

            {dashboardData?.independence_distribution ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <span style={{
                      fontSize: TappyLabsBranding.typography.scale.sm,
                      color: TappyBrandColors.primary.brandBlue,
                      fontFamily: TappyLabsBranding.typography.fonts.body
                    }}>Independent (70-100)</span>
                    <span style={{
                      fontSize: TappyLabsBranding.typography.scale.lg,
                      fontWeight: TappyLabsBranding.typography.weights.bold,
                      color: TappyBrandColors.semantic.success,
                      fontFamily: TappyLabsBranding.typography.fonts.headers
                    }}>{dashboardData.independence_distribution.independent}</span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '12px',
                    backgroundColor: TappyBrandColors.neutral.lightGray,
                    borderRadius: '6px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${(dashboardData.independence_distribution.independent / (dashboardData.independence_distribution.independent + dashboardData.independence_distribution.balanced + dashboardData.independence_distribution.dependent)) * 100}%`,
                      backgroundColor: TappyBrandColors.semantic.success,
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>

                <div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <span style={{
                      fontSize: TappyLabsBranding.typography.scale.sm,
                      color: TappyBrandColors.primary.brandBlue,
                      fontFamily: TappyLabsBranding.typography.fonts.body
                    }}>Balanced (40-69)</span>
                    <span style={{
                      fontSize: TappyLabsBranding.typography.scale.lg,
                      fontWeight: TappyLabsBranding.typography.weights.bold,
                      color: TappyBrandColors.primary.skyBlue,
                      fontFamily: TappyLabsBranding.typography.fonts.headers
                    }}>{dashboardData.independence_distribution.balanced}</span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '12px',
                    backgroundColor: TappyBrandColors.neutral.lightGray,
                    borderRadius: '6px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${(dashboardData.independence_distribution.balanced / (dashboardData.independence_distribution.independent + dashboardData.independence_distribution.balanced + dashboardData.independence_distribution.dependent)) * 100}%`,
                      backgroundColor: TappyBrandColors.primary.skyBlue,
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>

                <div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <span style={{
                      fontSize: TappyLabsBranding.typography.scale.sm,
                      color: TappyBrandColors.primary.brandBlue,
                      fontFamily: TappyLabsBranding.typography.fonts.body
                    }}>Dependent (0-39)</span>
                    <span style={{
                      fontSize: TappyLabsBranding.typography.scale.lg,
                      fontWeight: TappyLabsBranding.typography.weights.bold,
                      color: TappyBrandColors.accent.gold,
                      fontFamily: TappyLabsBranding.typography.fonts.headers
                    }}>{dashboardData.independence_distribution.dependent}</span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '12px',
                    backgroundColor: TappyBrandColors.neutral.lightGray,
                    borderRadius: '6px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${(dashboardData.independence_distribution.dependent / (dashboardData.independence_distribution.independent + dashboardData.independence_distribution.balanced + dashboardData.independence_distribution.dependent)) * 100}%`,
                      backgroundColor: TappyBrandColors.accent.gold,
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>

                <div style={{
                  marginTop: '0.5rem',
                  padding: '1rem',
                  backgroundColor: TappyBrandColors.neutral.offWhite,
                  borderRadius: '8px',
                  fontSize: TappyLabsBranding.typography.scale.xs,
                  color: TappyBrandColors.neutral.darkSlate,
                  fontFamily: TappyLabsBranding.typography.fonts.body
                }}>
                  Independent learners can work solo. Group them for peer teaching opportunities.
                </div>
              </div>
            ) : (
              <p style={{ color: TappyBrandColors.primary.brandBlue }}>No data yet</p>
            )}
          </div>

          {/* Question Quality Analysis */}
          <div style={{
            ...TappyLabsBranding.components.glassCard,
            padding: '2rem'
          }}>
            <h2 style={{
              fontSize: TappyLabsBranding.typography.scale.xl,
              fontWeight: TappyLabsBranding.typography.weights.bold,
              fontFamily: TappyLabsBranding.typography.fonts.headers,
              color: TappyBrandColors.neutral.darkSlate,
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center'
            }}>
              <HelpCircle style={{ marginRight: '0.5rem', color: TappyBrandColors.primary.darkTeal }} size={20} />
              Question Sophistication
            </h2>

            {dashboardData?.question_quality && dashboardData.question_quality.total_questions > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <span style={{
                      fontSize: TappyLabsBranding.typography.scale.sm,
                      color: TappyBrandColors.primary.brandBlue,
                      fontFamily: TappyLabsBranding.typography.fonts.body
                    }}>Conceptual ("why?")</span>
                    <span style={{
                      fontSize: TappyLabsBranding.typography.scale.lg,
                      fontWeight: TappyLabsBranding.typography.weights.bold,
                      color: TappyBrandColors.semantic.success,
                      fontFamily: TappyLabsBranding.typography.fonts.headers
                    }}>{dashboardData.question_quality.conceptual_pct}%</span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '12px',
                    backgroundColor: TappyBrandColors.neutral.lightGray,
                    borderRadius: '6px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${dashboardData.question_quality.conceptual_pct}%`,
                      backgroundColor: TappyBrandColors.semantic.success,
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>

                <div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <span style={{
                      fontSize: TappyLabsBranding.typography.scale.sm,
                      color: TappyBrandColors.primary.brandBlue,
                      fontFamily: TappyLabsBranding.typography.fonts.body
                    }}>Factual ("what?")</span>
                    <span style={{
                      fontSize: TappyLabsBranding.typography.scale.lg,
                      fontWeight: TappyLabsBranding.typography.weights.bold,
                      color: TappyBrandColors.primary.skyBlue,
                      fontFamily: TappyLabsBranding.typography.fonts.headers
                    }}>{dashboardData.question_quality.factual_pct}%</span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '12px',
                    backgroundColor: TappyBrandColors.neutral.lightGray,
                    borderRadius: '6px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${dashboardData.question_quality.factual_pct}%`,
                      backgroundColor: TappyBrandColors.primary.skyBlue,
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>

                <div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem'
                  }}>
                    <span style={{
                      fontSize: TappyLabsBranding.typography.scale.sm,
                      color: TappyBrandColors.primary.brandBlue,
                      fontFamily: TappyLabsBranding.typography.fonts.body
                    }}>Procedural ("how?")</span>
                    <span style={{
                      fontSize: TappyLabsBranding.typography.scale.lg,
                      fontWeight: TappyLabsBranding.typography.weights.bold,
                      color: TappyBrandColors.accent.gold,
                      fontFamily: TappyLabsBranding.typography.fonts.headers
                    }}>{dashboardData.question_quality.procedural_pct}%</span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '12px',
                    backgroundColor: TappyBrandColors.neutral.lightGray,
                    borderRadius: '6px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${dashboardData.question_quality.procedural_pct}%`,
                      backgroundColor: TappyBrandColors.accent.gold,
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>

                <div style={{
                  marginTop: '0.5rem',
                  padding: '1rem',
                  backgroundColor: TappyBrandColors.neutral.offWhite,
                  borderRadius: '8px',
                  fontSize: TappyLabsBranding.typography.scale.xs,
                  color: TappyBrandColors.neutral.darkSlate,
                  fontFamily: TappyLabsBranding.typography.fonts.body
                }}>
                  {dashboardData.question_quality.conceptual_pct >= 30
                    ? 'Great! Students are asking deep "why" questions.'
                    : dashboardData.question_quality.procedural_pct >= 60
                    ? 'Students are mostly asking "how to do" questions. Encourage more "why" thinking.'
                    : 'Balanced question types. Encourage more conceptual questions for deeper learning.'}
                </div>
              </div>
            ) : (
              <p style={{ color: TappyBrandColors.primary.brandBlue }}>No questions asked yet</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderLearningAnalytics = () => {
    // Determine if we have priority students from combined sources
    const hasPriorityStudents = dashboardData?.priority_students && dashboardData.priority_students.length > 0;
    const hasMisconceptions = dashboardData?.misconceptions && dashboardData.misconceptions.length > 0;
    const hasCommonQuestions = dashboardData?.common_questions && dashboardData.common_questions.length > 0;

    return (
      <div className="space-y-8">
        {/* Header - Teacher-Focused */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-3 tracking-tight">
            Class Overview
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl">
            Prioritize your attention and identify teaching opportunities
          </p>
        </div>

        {/* Period Toggle Buttons - Quick timeframe switching */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '2rem',
          padding: '0.5rem',
          backgroundColor: TappyBrandColors.neutral.paleGray,
          borderRadius: '12px',
          width: 'fit-content'
        }}>
          <button
            onClick={() => handlePeriodChange('week')}
            style={{
              padding: '0.5rem 1.5rem',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 600,
              transition: 'all 0.2s',
              backgroundColor: analyticsPeriod === 'week' ? TappyBrandColors.primary.teal : 'transparent',
              color: analyticsPeriod === 'week' ? 'white' : TappyBrandColors.neutral.darkSlate
            }}
          >
            This Week
          </button>
          <button
            onClick={() => handlePeriodChange('month')}
            style={{
              padding: '0.5rem 1.5rem',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 600,
              transition: 'all 0.2s',
              backgroundColor: analyticsPeriod === 'month' ? TappyBrandColors.primary.teal : 'transparent',
              color: analyticsPeriod === 'month' ? 'white' : TappyBrandColors.neutral.darkSlate
            }}
          >
            This Month
          </button>
          <button
            onClick={() => handlePeriodChange('all')}
            style={{
              padding: '0.5rem 1.5rem',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 600,
              transition: 'all 0.2s',
              backgroundColor: analyticsPeriod === 'all' ? TappyBrandColors.primary.teal : 'transparent',
              color: analyticsPeriod === 'all' ? 'white' : TappyBrandColors.neutral.darkSlate
            }}
          >
            All Time
          </button>
        </div>

        {/* Priority Students Banner - UNIFIED VIEW */}
        {hasPriorityStudents && (
          <div style={{
            padding: '1.5rem',
            backgroundColor: '#FEF3C7',
            border: '2px solid #F59E0B',
            borderRadius: '12px',
            marginBottom: '2rem'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '1rem'
            }}>
              <AlertTriangle style={{ marginRight: '0.75rem', color: '#F59E0B' }} size={24} />
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#1F2937',
                margin: 0
              }}>
                {dashboardData.priority_students.length} {dashboardData.priority_students.length === 1 ? 'Student Needs' : 'Students Need'} Your Attention {analyticsPeriod === 'week' ? 'This Week' : analyticsPeriod === 'month' ? 'This Month' : ''}
              </h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {dashboardData.priority_students.slice(0, 5).map((student) => (
                <div key={student.student_id} style={{
                  padding: '1rem',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'start'
                  }}>
                    <div>
                      <span style={{
                        fontWeight: '600',
                        color: '#1F2937',
                        fontSize: '0.95rem'
                      }}>{student.name}</span>
                      <div style={{
                        marginTop: '0.5rem',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.25rem'
                      }}>
                        {student.reasons.map((reason, idx) => (
                          <div key={idx} style={{
                            fontSize: '0.875rem',
                            color: '#6B7280',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            <span style={{
                              width: '4px',
                              height: '4px',
                              borderRadius: '50%',
                              backgroundColor: '#F59E0B'
                            }} />
                            {reason}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div style={{
                      textAlign: 'right',
                      fontSize: '0.875rem',
                      color: '#6B7280'
                    }}>
                      Independence: <strong>{student.independence_score}</strong>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Student Roster Table - MAIN COMPREHENSIVE VIEW */}
        {dashboardData?.student_roster && dashboardData.student_roster.length > 0 && (
          <StudentRosterTable students={dashboardData.student_roster} period={analyticsPeriod} />
        )}

      {/* Re-teach Opportunities - CONDITIONAL */}
      {hasMisconceptions && (
        <div style={{ marginTop: '3rem' }}>
          <div className="flex items-center gap-2 mb-4">
            <div className="h-1 w-8 bg-red-500 rounded-full"></div>
            <h2 className="text-xl font-semibold text-gray-900">Re-teach Opportunities</h2>
          </div>
          <MisconceptionAlert courseId={null} teacherId={user?.id} />
        </div>
      )}

      {/* Common Questions - CONDITIONAL */}
      {hasCommonQuestions && (
        <div style={{ marginTop: '3rem' }}>
          <CommonQuestions courseId={null} teacherId={user?.id} />
        </div>
      )}
    </div>
    );
  };

  const renderDesigner = () => (
    <div className="bg-white p-6 rounded-lg shadow">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold flex items-center">
          <Edit3 className="mr-2 text-indigo-600" size={20} />
          Curriculum Designer
        </h2>
        <button 
          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          onClick={() => setShowCreateModal(true)}
        >
          Add New Element
        </button>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Difficulty</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Topics</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bloom's Level</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentCurriculum.map((item) => (
              <tr key={item.id}>
                <td className="px-6 py-4 whitespace-nowrap">{item.title}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    item.difficulty === 'Easy' ? 'bg-green-100 text-green-800' : 
                    item.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 
                    'bg-red-100 text-red-800'
                  }`}>
                    {item.difficulty}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {item.topics.map(topic => (
                    <span key={topic} className="mr-1 px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs">
                      {topic}
                    </span>
                  ))}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                    {item.bloom}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button className="text-indigo-600 hover:text-indigo-900 mr-3">Edit</button>
                  <button className="text-red-600 hover:text-red-900">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderTrainer = () => (
    <div className="bg-white p-6 rounded-lg shadow">
      <h2 className="text-xl font-bold mb-6 flex items-center">
        <Zap className="mr-2 text-indigo-600" size={20} />
        AI Tutor Trainer
      </h2>
      
      <div className="grid grid-cols-2 gap-6 mb-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Subject</label>
            <select 
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
            >
              <option value="math">Mathematics</option>
              <option value="language-arts">Language Arts</option>
              <option value="science">Science</option>
              <option value="history">History</option>
              <option value="literature">Literature</option>
              <option value="chemistry">Chemistry</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Specialization</label>
            <select 
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              value={selectedSpecialization}
              onChange={(e) => setSelectedSpecialization(e.target.value)}
            >
              <option value="algebra">Algebra</option>
              <option value="geometry">Geometry</option>
              <option value="statistics">Statistics</option>
              <option value="essay-writing">Essay Writing</option>
              <option value="grammar">Grammar</option>
              <option value="reading-comprehension">Reading Comprehension</option>
              <option value="biology">Biology</option>
              <option value="chemistry">Chemistry</option>
              <option value="physics">Physics</option>
              <option value="world-history">World History</option>
              <option value="american-history">American History</option>
              <option value="poetry-analysis">Poetry Analysis</option>
              <option value="novel-study">Novel Study</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Base Model</label>
            <select className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
              <option>DeepSeek-Math-7B</option>
              <option>Phi-3-Math</option>
              <option>Llama-3-8B</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Training Method</label>
            <select className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
              <option>Morpheus RL + SFT</option>
              <option>RL Only</option>
              <option>SFT Only</option>
            </select>
          </div>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-medium mb-2">Advanced Configuration</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">LoRA Rank</label>
              <input type="range" min="8" max="64" step="8" defaultValue="16" className="w-full" />
              <div className="flex justify-between text-xs text-gray-500">
                <span>8</span>
                <span>16</span>
                <span>32</span>
                <span>64</span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Learning Rate</label>
              <input type="range" min="1" max="5" step="1" defaultValue="3" className="w-full" />
              <div className="flex justify-between text-xs text-gray-500">
                <span>2e-5</span>
                <span>2e-4</span>
                <span>2e-3</span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Training Steps</label>
              <input type="range" min="1" max="3" step="1" defaultValue="2" className="w-full" />
              <div className="flex justify-between text-xs text-gray-500">
                <span>500</span>
                <span>1000</span>
                <span>2000</span>
              </div>
            </div>
            
            <div className="flex items-center">
              <input type="checkbox" defaultChecked className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" />
              <label className="ml-2 block text-sm text-gray-700">Use Bloom's 2-Sigma Principles</label>
            </div>
          </div>
        </div>
      </div>
      
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-medium">Training Progress</h3>
          <span className="text-sm text-gray-600">{trainingProgress}% Complete</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div className="bg-indigo-600 h-2.5 rounded-full" style={{ width: `${trainingProgress}%` }}></div>
        </div>
      </div>
      
      <div className="mt-6 flex justify-between">
        <button className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
          Save Configuration
        </button>
        <button 
          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center"
          onClick={startTraining}
          disabled={isTraining}
        >
          {isTraining ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Training in Progress...
            </>
          ) : (
            <>Start Training</>
          )}
        </button>
      </div>
    </div>
  );

  // Modal for creating new curriculum elements
  const renderCreateModal = () => {
    if (!showCreateModal) return null;
    
    return (
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg max-w-md w-full p-6">
          <h2 className="text-xl font-bold mb-4">Add New Curriculum Element</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Title</label>
              <input type="text" className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500" placeholder="e.g., Solving Quadratic Equations" />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Difficulty</label>
              <select className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
                <option>Easy</option>
                <option>Medium</option>
                <option>Hard</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Topic</label>
              <select className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
                <option>Mathematics</option>
                <option>Language Arts</option>
                <option>Science</option>
                <option>History</option>
                <option>Literature</option>
                <option>Chemistry</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Bloom's Taxonomy Level</label>
              <select className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
                <option>Remember</option>
                <option>Understand</option>
                <option>Apply</option>
                <option>Analyze</option>
                <option>Evaluate</option>
                <option>Create</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500" rows="3" placeholder="Describe the problem and expected learning outcomes"></textarea>
            </div>
          </div>
          
          <div className="mt-6 flex justify-end space-x-3">
            <button 
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              onClick={() => setShowCreateModal(false)}
            >
              Cancel
            </button>
            <button 
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              onClick={() => {
                // Add new element logic would go here
                setShowCreateModal(false);
              }}
            >
              Add Element
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  // Modal for deployment options
  const renderDeploymentModal = () => {
    if (!showDeploymentModal || !selectedTutor) return null;
    
    return (
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
        <DeploymentOptions
          tutor={selectedTutor}
          deploymentType={deploymentType}
          onCancel={() => setShowDeploymentModal(false)}
          onSuccess={handleDeploymentSuccess}
        />
      </div>
    );
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${TappyLabsBranding.colors.primary.clarityWhite} 0%, ${TappyLabsBranding.colors.transparency.subtle} 100%)`,
      fontFamily: TappyLabsBranding.typography.fonts.body
    }}>
      {/* Header - iOS-style Glassmorphism */}
      <header style={{
        backgroundColor: 'rgba(252, 253, 254, 0.85)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        borderBottom: '1px solid rgba(213, 222, 221, 0.5)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 1px 3px rgba(26, 62, 72, 0.08)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 40px',
          minHeight: '88px',
          maxHeight: '88px',
          width: '100%'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <img
              src="/images/TAPPY-logo-black.png"
              alt="Teach Tappy Logo"
              style={{
                height: '32px',
                width: 'auto',
                objectFit: 'contain'
              }}
            />
            <div>
              <h1 style={{
                fontSize: '24px',
                fontWeight: 600,
                fontFamily: TappyLabsBranding.typography.fonts.headers,
                color: '#1A3E48',
                margin: 0,
                letterSpacing: '-0.5px'
              }}>Teacher Dashboard</h1>
              {isAdmin && (
                <span style={{
                  marginLeft: '0.5rem',
                  padding: '0.25rem 0.5rem',
                  backgroundColor: TappyLabsBranding.colors.semantic.error,
                  color: '#ffffff',
                  fontSize: TappyLabsBranding.typography.scale.xs,
                  borderRadius: '9999px'
                }}>
                  Admin
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {/* Profile Dropdown */}
            <div style={{ position: 'relative' }} ref={profileDropdownRef}>
              <button
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                style={{
                  background: 'rgba(255, 255, 255, 0.6)',
                  border: '1px solid rgba(213, 222, 221, 0.5)',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  color: '#1A3E48',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontSize: '14px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.6)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <User className="h-4 w-4" />
                <span>{user?.name || user?.username || 'Teacher'}</span>
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showProfileDropdown && (
                <div style={{
                  position: 'absolute',
                  right: 0,
                  marginTop: '0.5rem',
                  width: '16rem',
                  background: TappyLabsBranding.colors.primary.clarityWhite,
                  borderRadius: '0.75rem',
                  boxShadow: '0 8px 32px rgba(15, 23, 42, 0.1)',
                  border: `1px solid rgba(255, 255, 255, 0.2)`,
                  zIndex: 50
                }}>
                  <div style={{ padding: '0.5rem 0' }}>
                    {/* User Info Header */}
                    <div style={{
                      padding: '1rem',
                      borderBottom: `1px solid ${TappyLabsBranding.colors.transparency.subtle}`
                    }}>
                      <div style={{
                        fontSize: TappyLabsBranding.typography.scale.sm,
                        fontWeight: TappyLabsBranding.typography.weights.semibold,
                        color: TappyLabsBranding.colors.primary.deepOcean
                      }}>
                        {user?.name || user?.username || 'Teacher'}
                      </div>
                      <div style={{
                        fontSize: TappyLabsBranding.typography.scale.xs,
                        color: TappyLabsBranding.colors.semantic.neutral
                      }}>
                        Teacher Dashboard
                      </div>
                    </div>

                    {/* Menu Items */}
                    <button
                      onClick={() => handleDropdownAction('upload-essay')}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0.75rem 1rem',
                        textAlign: 'left',
                        fontSize: TappyLabsBranding.typography.scale.sm,
                        color: TappyLabsBranding.colors.semantic.neutral,
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = TappyLabsBranding.colors.transparency.subtle;
                        e.target.style.color = TappyLabsBranding.colors.primary.learningAquariumBlue;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'transparent';
                        e.target.style.color = TappyLabsBranding.colors.semantic.neutral;
                      }}
                    >
                      <svg style={{ marginRight: '0.75rem', width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      Upload Essays for Analysis
                    </button>

                    <button
                      onClick={() => handleDropdownAction('my-essays')}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0.75rem 1rem',
                        textAlign: 'left',
                        fontSize: TappyLabsBranding.typography.scale.sm,
                        color: TappyLabsBranding.colors.semantic.neutral,
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = TappyLabsBranding.colors.transparency.subtle;
                        e.target.style.color = TappyLabsBranding.colors.primary.learningAquariumBlue;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'transparent';
                        e.target.style.color = TappyLabsBranding.colors.semantic.neutral;
                      }}
                    >
                      <svg style={{ marginRight: '0.75rem', width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      My Essays & Results
                    </button>

                    <button
                      onClick={() => handleDropdownAction('account-settings')}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0.75rem 1rem',
                        textAlign: 'left',
                        fontSize: TappyLabsBranding.typography.scale.sm,
                        color: TappyLabsBranding.colors.semantic.neutral,
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = TappyLabsBranding.colors.transparency.subtle;
                        e.target.style.color = TappyLabsBranding.colors.primary.learningAquariumBlue;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'transparent';
                        e.target.style.color = TappyLabsBranding.colors.semantic.neutral;
                      }}
                    >
                      <svg style={{ marginRight: '0.75rem', width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Account Settings
                    </button>

                    <div style={{
                      borderTop: `1px solid ${TappyLabsBranding.colors.transparency.subtle}`,
                      marginTop: '0.5rem',
                      paddingTop: '0.5rem'
                    }}>
                      <button
                        onClick={() => handleDropdownAction('logout')}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0.75rem 1rem',
                          textAlign: 'left',
                          fontSize: TappyLabsBranding.typography.scale.sm,
                          color: TappyLabsBranding.colors.semantic.error,
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = `${TappyLabsBranding.colors.semantic.error}10`;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'transparent';
                        }}
                      >
                        <LogOut style={{ marginRight: '0.75rem', width: '1.25rem', height: '1.25rem' }} />
                        Logout
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      
      {/* Main content */}
      <main style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '2rem 1rem',
        minHeight: 'calc(100vh - 88px)'
      }}>
        {/* Tabs - 2025 Design: Simplified Navigation */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: `1px solid ${TappyLabsBranding.colors.transparency.medium}`,
          marginBottom: '2rem',
          gap: '0.5rem'
        }}>
          {/* Active Tabs */}
          <button
            style={{
              padding: '1rem 1.5rem',
              fontFamily: TappyLabsBranding.typography.fonts.body,
              fontSize: TappyLabsBranding.typography.scale.base,
              fontWeight: activeTab === 'dashboard' ? TappyLabsBranding.typography.weights.semibold : TappyLabsBranding.typography.weights.medium,
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderBottom: activeTab === 'dashboard' ? `3px solid ${TappyLabsBranding.colors.primary.learningAquariumBlue}` : '3px solid transparent',
              color: activeTab === 'dashboard' ? TappyLabsBranding.colors.primary.learningAquariumBlue : TappyLabsBranding.colors.semantic.neutral,
              transition: 'all 0.2s ease',
              marginBottom: '-1px'
            }}
            onClick={() => navigateToTab('dashboard')}
            onMouseEnter={(e) => {
              if (activeTab !== 'dashboard') {
                e.target.style.color = TappyLabsBranding.colors.primary.deepOcean;
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'dashboard') {
                e.target.style.color = TappyLabsBranding.colors.semantic.neutral;
              }
            }}
          >
            Dashboard
          </button>
          <button
            style={{
              padding: '1rem 1.5rem',
              fontFamily: TappyLabsBranding.typography.fonts.body,
              fontSize: TappyLabsBranding.typography.scale.base,
              fontWeight: activeTab === 'analytics' ? TappyLabsBranding.typography.weights.semibold : TappyLabsBranding.typography.weights.medium,
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderBottom: activeTab === 'analytics' ? `3px solid ${TappyLabsBranding.colors.primary.learningAquariumBlue}` : '3px solid transparent',
              color: activeTab === 'analytics' ? TappyLabsBranding.colors.primary.learningAquariumBlue : TappyLabsBranding.colors.semantic.neutral,
              transition: 'all 0.2s ease',
              marginBottom: '-1px'
            }}
            onClick={() => navigateToTab('analytics')}
            onMouseEnter={(e) => {
              if (activeTab !== 'analytics') {
                e.target.style.color = TappyLabsBranding.colors.primary.deepOcean;
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'analytics') {
                e.target.style.color = TappyLabsBranding.colors.semantic.neutral;
              }
            }}
          >
            Class Overview
          </button>
        </div>

        {/*
          ============================================================================
          FUTURE TABS - Preserved for Phase 4 Implementation
          ============================================================================
          TODO: Uncomment when ready to implement these features

          Features planned:
          - Curriculum Designer: Custom learning paths
          - AI Model Training: Fine-tune Mira for your school
          - Tutor Marketplace: Browse and deploy specialized tutors
          - Deployment: LMS integration and rollout
          - Token Economy: Student rewards and incentives
          - Strategy Exchange: Share teaching strategies
        */}
        
        {/* Tab content - Active Features */}
        {activeTab === 'dashboard' && (
          <>
            {/* Class materials & assignments — the same notebook outcome
                loop as the free teacher tier. Self-contained (fetches its
                own data, teacher-auth APIs). Mounted here because school-
                licensed teachers previously had NO notebook UI at all —
                upgrading a school silently deleted the workflow — and it
                needs zero LMS integration, which also gives teachers with
                incomplete Schoology/Clever section links a same-day way
                to assign materials and see per-student outcomes. */}
            <div style={{ maxWidth: 1100, margin: '0 auto 24px' }}>
              <NotebookPanel />
            </div>
            {renderDashboard()}
          </>
        )}
        {activeTab === 'analytics' && renderLearningAnalytics()}

        {/*
          FUTURE TAB CONTENT - Removed for Phase 3B clean release

          Preserved in git history (commit before this change):
          - Curriculum Designer tab (renderDesigner)
          - AI Model Training tab (renderTrainer)
          - Tutor Marketplace tab
          - Token Economy tab
          - Deployment Options tab
          - Strategy Exchange tab

          TODO Phase 4: Restore these tabs when features are implemented
        */}
        {activeTab === 'token-marketplace' && (
          <div className="bg-white p-6 rounded-lg shadow mb-6">
            <h2 className="text-xl font-bold mb-6 flex items-center">
              <Gift className="mr-2 text-indigo-600" size={20} />
              Strategy Marketplace
            </h2>
            <TokenMarketplace userId={user?.id || 'user123'} onStrategyPurchase={(strategy) => {
              // Handle strategy purchase success
              console.log("Strategy purchased:", strategy);
            }} />
          </div>
        )}
        {activeTab === 'tokens' && (
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-bold mb-6 flex items-center">
              <Coins className="mr-2 text-indigo-600" size={20} />
              Token Economy Dashboard
            </h2>
            
            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                The Morpheus-RL reward system connects Discord and LMS platforms, creating a unified system that incentivizes learning and contribution.
              </p>
              
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100 mb-6">
                <h3 className="text-md font-semibold text-indigo-800 mb-2">Cross-Platform Integration</h3>
                <p className="text-sm text-indigo-700">
                  Students earn rewards in Discord that can be displayed in your LMS, and achievements in your LMS 
                  are recognized in Discord. The system is flexible and can be adapted to your institution's policies.
                </p>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-100 mb-6">
                <h3 className="text-md font-semibold text-yellow-800 mb-2">Institutional Settings</h3>
                <p className="text-sm text-yellow-700">
                  For educational institutions, you can enable "Institutional Mode" to rename tokens to points/badges, 
                  control what's visible in the LMS interface, and adjust how the reward system operates in your environment.
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-6 mb-6">
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                    <Award className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-medium">Achievement Rewards</h3>
                    <p className="text-sm text-gray-500">Rewards for completing milestones</p>
                  </div>
                </div>
                <button 
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  onClick={() => window.open('/api/token/achievements', '_blank')}
                >
                  Manage Achievements
                </button>
              </div>
              
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                    <Gift className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-medium">Bounty Rewards</h3>
                    <p className="text-sm text-gray-500">Rewards for completing challenges</p>
                  </div>
                </div>
                <button 
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  onClick={() => window.open('/api/token/bounties', '_blank')}
                >
                  Manage Bounties
                </button>
              </div>
              
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center mr-3">
                    <Brain className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-medium">Training Rewards</h3>
                    <p className="text-sm text-gray-500">Rewards for model contributions</p>
                  </div>
                </div>
                <button 
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  onClick={() => window.open('/api/token/training', '_blank')}
                >
                  Manage Training Rewards
                </button>
              </div>
            </div>
            
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">Student Token Activity</h3>
              <div className="bg-gray-50 p-1 rounded-lg">
                <TokenActivity userId="demo_student1" />
              </div>
            </div>
            
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Token Economy Settings</h3>
              <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                Save Settings
              </button>
            </div>
            
            <div className="bg-white p-6 rounded-lg border border-gray-200 mb-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Institutional Mode
                    </label>
                    <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
                      <option value="false">Disabled - Show Tokens</option>
                      <option value="true">Enabled - Use Points/Badges</option>
                    </select>
                    {isAdmin && (
                      <p className="mt-1 text-xs text-gray-500">
                        Institutional mode changes "Tokens" to "Points" and modifies the interface for educational settings.
                      </p>
                    )}
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cross-Platform Transfers
                    </label>
                    <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
                      <option>Enabled</option>
                      <option>Disabled</option>
                    </select>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Points/Tokens Display Name
                    </label>
                    <input
                      type="text"
                      className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                      defaultValue="Tokens"
                      placeholder="e.g., Points, Credits, Stars"
                    />
                  </div>
                </div>
                
                <div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Show Balance in LMS
                    </label>
                    <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
                      <option value="true">Yes - Display in LMS</option>
                      <option value="false">No - Only in Discord</option>
                    </select>
                  </div>
                
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Min Transfer Amount
                    </label>
                    <input
                      type="number"
                      className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                      defaultValue={5}
                      min={1}
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Achievement Notifications
                    </label>
                    <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
                      <option value="true">Enabled</option>
                      <option value="false">Disabled</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'deployment' && (
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-bold mb-6 flex items-center">
              <Share2 className="mr-2 text-indigo-600" size={20} />
              Deploy Your AI Tutors
            </h2>
            
            {/* Deployment Options */}
            <div className="grid grid-cols-3 gap-6 mb-8">
              <div className="border border-gray-200 rounded-lg p-4 hover:border-indigo-500 hover:shadow-md transition-all cursor-pointer">
                <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Database className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-medium mb-2">Local Deployment</h3>
                <p className="text-sm text-gray-600 mb-4">Deploy your tutor models locally for testing or small-scale use.</p>
                <button className="text-sm text-indigo-600 font-medium">Learn more</button>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-4 hover:border-indigo-500 hover:shadow-md transition-all cursor-pointer">
                <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <Code className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="font-medium mb-2">AWS Deployment</h3>
                <p className="text-sm text-gray-600 mb-4">Scale your tutors using Amazon SageMaker or EC2 instances.</p>
                <button className="text-sm text-indigo-600 font-medium">Set up now</button>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-4 hover:border-indigo-500 hover:shadow-md transition-all cursor-pointer">
                <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <Sliders className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="font-medium mb-2">API Integration</h3>
                <p className="text-sm text-gray-600 mb-4">Connect your tutors to existing LMS platforms via API.</p>
                <button className="text-sm text-indigo-600 font-medium">View documentation</button>
              </div>
            </div>
            
            {/* Tabs for different deployment options */}
            <div className="border-t border-gray-200 pt-6">
              <ul className="flex flex-wrap text-sm font-medium text-center border-b border-gray-200">
                <li className="mr-2">
                  <button 
                    onClick={() => setDeploymentTab('integration')} 
                    className={`inline-block p-4 rounded-t-lg ${
                      deploymentTab === 'integration' 
                        ? 'text-indigo-600 bg-gray-100 border-indigo-600 border-b-2'
                        : 'hover:text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    LMS Integration
                  </button>
                </li>
                <li className="mr-2">
                  <button 
                    onClick={() => setDeploymentTab('models')} 
                    className={`inline-block p-4 rounded-t-lg ${
                      deploymentTab === 'models' 
                        ? 'text-indigo-600 bg-gray-100 border-indigo-600 border-b-2'
                        : 'hover:text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Deployed Models
                  </button>
                </li>
                <li className="mr-2">
                  <button 
                    onClick={() => setDeploymentTab('monitoring')} 
                    className={`inline-block p-4 rounded-t-lg ${
                      deploymentTab === 'monitoring' 
                        ? 'text-indigo-600 bg-gray-100 border-indigo-600 border-b-2'
                        : 'hover:text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center">
                      <Activity className="h-4 w-4 mr-1" />
                      Monitoring
                    </div>
                  </button>
                </li>
              </ul>
              
              <div className="pt-4">
                {/* LMS Deployment Component */}
                {deploymentTab === 'integration' && (
                  <LMSDeployment 
                    tutorName="Multi-Subject AI Tutor" 
                    tutorId="algebra-master-v2"
                    onDeploy={(data) => {
                      console.log('Model deployed:', data);
                      setDeploymentTab('monitoring');
                    }} 
                  />
                )}
                
                {/* Deployed Models List */}
                {deploymentTab === 'models' && (
                  <div className="mt-4">
                    <h3 className="font-medium mb-4">Deployed Models</h3>
                    
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Model Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deployment</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap">Mathematics Tutor</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                              Canvas LMS
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs flex items-center w-16">
                              <span className="h-2 w-2 bg-green-500 rounded-full mr-1"></span>
                              Active
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button 
                              className="text-indigo-600 hover:text-indigo-900 mr-3"
                              onClick={() => setDeploymentTab('monitoring')}
                            >
                              Monitor
                            </button>
                            <button className="text-gray-600 hover:text-gray-900 mr-3">Clone</button>
                            <button className="text-red-600 hover:text-red-900">Stop</button>
                          </td>
                        </tr>
                        
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap">Science Tutor</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                              Schoology
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs flex items-center w-16">
                              <span className="h-2 w-2 bg-green-500 rounded-full mr-1"></span>
                              Active
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button 
                              className="text-indigo-600 hover:text-indigo-900 mr-3"
                              onClick={() => setDeploymentTab('monitoring')}
                            >
                              Monitor
                            </button>
                            <button className="text-gray-600 hover:text-gray-900 mr-3">Clone</button>
                            <button className="text-red-600 hover:text-red-900">Stop</button>
                          </td>
                        </tr>
                        
                        <tr>
                          <td className="px-6 py-4 whitespace-nowrap">Language Arts Tutor</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                              Blackboard
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs flex items-center w-24">
                              <span className="h-2 w-2 bg-yellow-500 rounded-full mr-1"></span>
                              Deploying
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button className="text-indigo-600 hover:text-indigo-900 mr-3 opacity-50 cursor-not-allowed">Monitor</button>
                            <button className="text-gray-600 hover:text-gray-900 mr-3 opacity-50 cursor-not-allowed">Clone</button>
                            <button className="text-red-600 hover:text-red-900">Cancel</button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    
                    <div className="mt-6 flex justify-end">
                      <button 
                        className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                        onClick={() => setDeploymentTab('integration')}
                      >
                        Deploy New Model
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Monitoring Dashboard */}
                {deploymentTab === 'monitoring' && (
                  <div className="mt-4">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-medium">Model Monitoring Dashboard</h3>
                      <button 
                        className="text-sm text-indigo-600 font-medium flex items-center"
                        onClick={() => window.open('/api/lms/monitoring/report/pdf', '_blank')}
                      >
                        <svg className="h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7 10 12 15 17 10"></polyline>
                          <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        Export Report
                      </button>
                    </div>
                    
                    <LMSMonitoring 
                      selectedModels={[
                        {
                          id: "model_101",
                          name: "Mathematics Tutor",
                          model_type: "neo",
                          specialty: "algebra",
                          status: "active"
                        },
                        {
                          id: "model_102",
                          name: "Calculus Guide",
                          model_type: "grpo",
                          specialty: "calculus",
                          status: "active"
                        }
                      ]}
                    />
                  </div>
                )}
              </div>
            </div>
            
            {/* Deployed Models List - Hidden for now in favor of tabbed interface */}
            <div className="hidden mt-8 border-t border-gray-200 pt-6">
              <h3 className="font-medium mb-4">Deployed Models</h3>
              
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Model Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deployment</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap">Algebra Master</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                        AWS SageMaker
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs flex items-center w-16">
                        <span className="h-2 w-2 bg-green-500 rounded-full mr-1"></span>
                        Active
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button className="text-indigo-600 hover:text-indigo-900 mr-3">Edit</button>
                      <button className="text-gray-600 hover:text-gray-900 mr-3">Clone</button>
                      <button className="text-red-600 hover:text-red-900">Stop</button>
                    </td>
                  </tr>
                  
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap">Calculus Guide</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                        Local Deployment
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs flex items-center w-16">
                        <span className="h-2 w-2 bg-green-500 rounded-full mr-1"></span>
                        Active
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button className="text-indigo-600 hover:text-indigo-900 mr-3">Edit</button>
                      <button className="text-gray-600 hover:text-gray-900 mr-3">Clone</button>
                      <button className="text-red-600 hover:text-red-900">Stop</button>
                    </td>
                  </tr>
                  
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap">Geometry Explorer</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                        API Integration
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs flex items-center w-24">
                        <span className="h-2 w-2 bg-yellow-500 rounded-full mr-1"></span>
                        Deploying
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button className="text-indigo-600 hover:text-indigo-900 mr-3">Edit</button>
                      <button className="text-gray-600 hover:text-gray-900 mr-3">Clone</button>
                      <button className="text-red-600 hover:text-red-900">Cancel</button>
                    </td>
                  </tr>
                </tbody>
              </table>
              
              <div className="mt-6 flex justify-end">
                <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                  Deploy New Model
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      
      {/* Create Modal */}
      {renderCreateModal()}
      
      {/* Deployment Modal */}
      {renderDeploymentModal()}
      
      {/* Footer */}
      <footer style={{
        backgroundColor: TappyLabsBranding.colors.primary.clarityWhite,
        borderTop: `1px solid ${TappyLabsBranding.colors.transparency.subtle}`,
        padding: '2rem 0',
        marginTop: 'auto'
      }}>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: '0.5rem',
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 1rem'
        }}>
          <img 
            src="/images/TAPPY-logo-black.png" 
            alt="Tappy Labs Logo" 
            style={{
              height: '24px',
              width: 'auto',
              objectFit: 'contain',
              marginBottom: '0.5rem'
            }}
          />
          <p style={{
            margin: 0,
            fontSize: TappyLabsBranding.typography.scale.sm,
            color: TappyLabsBranding.colors.primary.deepOcean,
            fontFamily: TappyLabsBranding.typography.fonts.body
          }}>
            © 2026 Teach Tappy
          </p>
          <p style={{
            margin: 0,
            fontSize: TappyLabsBranding.typography.scale.xs,
            color: TappyLabsBranding.colors.semantic.neutral,
            fontFamily: TappyLabsBranding.typography.fonts.body
          }}>
            Tappy is powered by <span style={{fontWeight: TappyLabsBranding.typography.weights.medium}}>Morpheus RL</span> technology
          </p>
          <p style={{
            margin: 0,
            fontSize: TappyLabsBranding.typography.scale.xs,
            color: TappyLabsBranding.colors.semantic.neutral,
            fontFamily: TappyLabsBranding.typography.fonts.body,
            lineHeight: 1.4
          }}>
            By using our services, you agree to Tappy's{' '}
            <a
              href="/terms-of-service.html"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: TappyLabsBranding.colors.semantic.neutral,
                textDecoration: 'underline'
              }}
            >
              Terms of Use
            </a>
            {' '}and{' '}
            <a
              href="/privacy-policy.html"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: TappyLabsBranding.colors.semantic.neutral,
                textDecoration: 'underline'
              }}
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </footer>

      {/* Essay Upload Modal */}
      {showEssayUpload && (
        <EssayUpload
          onClose={() => setShowEssayUpload(false)}
          onUploadSuccess={(fileInfo) => {
            console.log('Essay uploaded successfully:', fileInfo);
            // Future: Add to dashboard or analytics
          }}
        />
      )}

      {/* Essay Status Modal */}
      {showEssayStatus && (
        <EssayStatus
          onClose={() => setShowEssayStatus(false)}
        />
      )}

      {/* Alert Toast Notification */}
      {showAlert && (
        <ToastNotification
          message={alertMessage}
          type={alertType}
          duration={8000}
          show={showAlert}
          onClose={() => setShowAlert(false)}
          actionButton={{
            label: 'View Details',
            onClick: () => {
              navigateToTab('analytics');
              setShowAlert(false);
            }
          }}
        />
      )}
    </div>
  );
};

export default MorpheusInterface;